import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './marketing.css';

/* ── Animated counter hook ─────────────────────────── */
function useCountUp(target, duration = 2200, start = false) {
 const [count, setCount] = useState(0);
 useEffect(() => {
 if (!start) return;
 let startTime = null;
 const step = (timestamp) => {
 if (!startTime) startTime = timestamp;
 const progress = Math.min((timestamp - startTime) / duration, 1);
 const ease = 1 - Math.pow(1 - progress, 3);
 setCount(Math.floor(ease * target));
 if (progress < 1) requestAnimationFrame(step);
 };
 requestAnimationFrame(step);
 }, [target, duration, start]);
 return count;
}

/* ── Intersection observer hook ────────────────────── */
function useInView(threshold = 0.2) {
 const ref = useRef(null);
 const [inView, setInView] = useState(false);
 useEffect(() => {
 const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
 if (ref.current) obs.observe(ref.current);
 return () => obs.disconnect();
 }, [threshold]);
 return [ref, inView];
}

/* ── Scroll reveal hook ────────────────────────────── */
function useScrollReveal() {
 useEffect(() => {
 const els = document.querySelectorAll('.anim');
 const io = new IntersectionObserver((entries) => {
 entries.forEach(entry => {
 if (entry.isIntersecting) {
 entry.target.classList.add('visible');
 io.unobserve(entry.target);
 }
 });
 }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
 els.forEach(el => io.observe(el));
 return () => io.disconnect();
 }, []);
}

export default function Home() {
 const navigate = useNavigate();
 const [menuOpen, setMenuOpen] = useState(false);
 const [modalOpen, setModalOpen] = useState(false);
 const [formStatus, setFormStatus] = useState('idle');
 const [selectedTopics, setSelectedTopics] = useState([]);
 const [faqOpen, setFaqOpen] = useState(null);
 const [industriesOpen, setIndustriesOpen] = useState(false);
 const [activeIndustry, setActiveIndustry] = useState(null);
 const [chartAnimated, setChartAnimated] = useState(false);
 const [chartTotal, setChartTotal] = useState(0);
 const [feedItems, setFeedItems] = useState([
 { dot: 'var(--mkt-green)', text: 'Invoice #4821 auto-processed', time: '2s ago' },
 { dot: 'var(--mkt-blue)', text: 'Support ticket resolved by AI', time: '8s ago' },
 { dot: 'var(--mkt-violet)', text: 'Sales data synced to CRM', time: '15s ago' },
 ]);
 const [formData, setFormData] = useState({
 name: '', company: '', email: '', phone: '', description: '', teamSize: '', budget: ''
 });

 const [statsRef, statsInView] = useInView(0.3);
 const c1 = useCountUp(500, 2200, statsInView);
 const c2 = useCountUp(150, 2200, statsInView);
 const c3 = useCountUp(12, 2200, statsInView);
 const c4 = useCountUp(98, 2200, statsInView);

 // Promo video state
 const promoVideoRef = useRef(null);
 const [promoMuted, setPromoMuted] = useState(true);
 const [promoPlaying, setPromoPlaying] = useState(true);
 const [promoError, setPromoError] = useState(false);

 function togglePromoMute() {
 if (!promoVideoRef.current) return;
 promoVideoRef.current.muted = !promoVideoRef.current.muted;
 setPromoMuted(promoVideoRef.current.muted);
 if (promoVideoRef.current.paused) { promoVideoRef.current.play(); setPromoPlaying(true); }
 }
 function togglePromoPlay() {
 if (!promoVideoRef.current) return;
 if (promoVideoRef.current.paused) { promoVideoRef.current.play(); setPromoPlaying(true); }
 else { promoVideoRef.current.pause(); setPromoPlaying(false); }
 }

 // Transformation section counters
 const [transformRef, transformInView] = useInView(0.25);
 const t1 = useCountUp(520, 2400, transformInView);
 const t2 = useCountUp(240, 2400, transformInView);
 const t3 = useCountUp(94, 2400, transformInView);
 const t4 = useCountUp(14, 2400, transformInView);

 useScrollReveal();

 // Chart animation on load
 useEffect(() => {
 const t = setTimeout(() => {
 setChartAnimated(true);
 // Count up total
 let cur = 0;
 const target = 114;
 const interval = setInterval(() => {
 cur += 3;
 if (cur >= target) { cur = target; clearInterval(interval); }
 setChartTotal(cur);
 }, 30);
 }, 800);
 return () => clearTimeout(t);
 }, []);

 // Live feed rotation
 useEffect(() => {
 const messages = [
 { dot: 'var(--mkt-green)', text: 'Invoice #4822 auto-approved', time: 'just now' },
 { dot: 'var(--mkt-blue)', text: 'Support ticket #892 resolved by AI', time: '1s ago' },
 { dot: 'var(--mkt-violet)', text: 'HR onboarding triggered for new hire', time: 'just now' },
 { dot: '#f59e0b', text: 'Daily KPI report sent to CEO', time: '3s ago' },
 { dot: 'var(--mkt-blue)', text: 'New lead routed to sales pipeline', time: 'just now' },
 { dot: '#06b6d4', text: 'Trading bot executed BTC/USD order', time: '2s ago' },
 { dot: 'var(--mkt-green)', text: 'Contract NDA-2841 reviewed by AI', time: 'just now' },
 { dot: 'var(--mkt-green)', text: 'Email classified and forwarded by AI', time: '1s ago' },
 ];
 let idx = 0;
 const interval = setInterval(() => {
 const msg = messages[idx % messages.length];
 idx++;
 setFeedItems(prev => [msg, prev[0], prev[1]]);
 }, 3000);
 return () => clearInterval(interval);
 }, []);

 useEffect(() => {
 const onKey = (e) => { if (e.key === 'Escape') setModalOpen(false); };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, []);

 useEffect(() => {
 document.body.style.overflow = modalOpen ? 'hidden' : '';
 }, [modalOpen]);

 function navTo(id) {
 const el = document.getElementById(id);
 if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
 setMenuOpen(false);
 }

 function toggleTopic(t) {
 setSelectedTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
 }

 async function handleSubmit(e) {
 e.preventDefault();
 setFormStatus('sending');
 try {
 const payload = { ...formData, topics: selectedTopics.join(', '), source: 'gantima.com' };
 const res = await fetch('https://uej8pgkdj2.execute-api.us-east-1.amazonaws.com/prod/consultation', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
 body: JSON.stringify(payload),
 });
 if (res.ok) {
 setFormStatus('success');
 setTimeout(() => { setModalOpen(false); setFormStatus('idle'); }, 3000);
 } else { setFormStatus('error'); }
 } catch { setFormStatus('error'); }
 }

 function handleInput(e) {
 setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
 }

 /* ── Data ───────────────────────────────────────── */
 const topics = ['Workflow Automation','AI Chatbot','Data Processing','Document AI','CRM Automation','Email Automation','HR Automation','Finance Automation','Trading Strategy Automation','Algo Trading Bot','Market Data Pipeline','Risk Management AI'];

 const services = [
 { icon: '', title: 'Business Process Automation', desc: 'Eliminate repetitive manual tasks with intelligent workflows. We automate data entry, approvals, reporting, and cross-system operations — saving your team hundreds of hours per month.', tags: ['Zapier/Make', 'Python Bots', 'RPA', 'API Integration'], color: '#4f8ef7' },
 { icon: '', title: 'AI Chatbots & Assistants', desc: 'Deploy 24/7 AI assistants trained on your business data. From customer support to internal knowledge bases, our chatbots resolve queries instantly and learn continuously.', tags: ['GPT-4 Powered', 'Multi-channel', 'Custom Training', 'Analytics'], color: '#7c3aed' },
 { icon: '', title: 'AI-Powered SaaS Products', desc: 'We design and build complete AI-native software products — from concept to launch. Full-stack development with intelligent features baked in from day one.', tags: ['React / Node', 'AWS / GCP', 'ML Integration', 'Scalable Infra'], color: '#06b6d4' },
 { icon: '', title: 'AI Strategy & Consulting', desc: 'Not sure where to start with AI? Our team audits your current workflows, identifies the highest-ROI automation opportunities, and delivers a custom AI transformation roadmap.', tags: ['Audit & Analysis', 'ROI Modeling', 'Roadmap', 'Change Mgmt'], color: '#f59e0b' },
 { icon: '', title: 'Trading Strategy Automation', desc: 'We build fully automated trading systems — from signal generation and backtesting to live execution and risk management. Custom algo bots for crypto, forex, stocks, and futures.', tags: ['Algo Trading', 'Backtesting', 'Live Execution', 'Risk AI'], color: '#10b981' },
 { icon: '', title: 'Stock Trading Intelligence', desc: 'Explainable decision-support for U.S. stock traders. Ranked A-F setups, transparent 0-100 opportunity scoring, disciplined position sizing, and plain-English rationale for every signal. No black boxes.', tags: ['A-F Setup Scanner', 'Opportunity Score', 'Risk Sizing', 'Investiy'], color: '#a3e635', badge: 'Powered by Investiy' },
 { icon: '', title: 'AI Social Media Marketing', desc: 'Dominate every social platform with AI. We automate your entire social presence — viral captions, hashtags, content calendars, and growth analytics across Instagram, TikTok, X, LinkedIn and more.', tags: ['Viral Content AI', 'Hashtag Engine', 'Multi-Platform', 'ViralVolt '], color: '#ec4899', badge: 'Powered by ViralVolt' },
 ];

 const techStack = [
 { name: 'OpenAI GPT-4', icon: '' }, { name: 'Make.com', icon: '' }, { name: 'Zapier', icon: '' },
 { name: 'Python', icon: '' }, { name: 'AWS', icon: '' }, { name: 'React', icon: '' },
 { name: 'Node.js', icon: '🟢' }, { name: 'Binance API', icon: '' }, { name: 'HubSpot', icon: '' },
 { name: 'Salesforce', icon: '' }, { name: 'Slack', icon: '' }, { name: 'Stripe', icon: '' },
 { name: 'MongoDB', icon: '' }, { name: 'Anthropic', icon: '' }, { name: 'LangChain', icon: '' },
 ];

 const whyUs = [
 { icon: '', title: 'Ship in 2–4 Weeks', desc: 'Not months. We have pre-built connectors and battle-tested templates that slash build time — without cutting corners on quality.', vs: 'Others: 3–6 months' },
 { icon: '', title: 'ROI-First Approach', desc: 'Every project starts with an ROI model. We only build automations that will pay for themselves — usually within 60 days.', vs: 'Others: Bill hours, hope for results' },
 { icon: '', title: 'Dedicated AI Engineer', desc: 'You get a single engineer who knows your business inside-out. No ticket queues, no account managers — direct access to your builder.', vs: 'Others: Rotating support teams' },
 { icon: '', title: 'Enterprise Security', desc: 'SOC 2-compliant workflows, encrypted credentials, and data never leaves your infrastructure without your explicit consent.', vs: 'Others: Shared cloud environments' },
 { icon: '', title: 'Live Performance Dashboard', desc: "Every automation comes with a real-time dashboard showing exactly how many hours and dollars it's saving you — updated daily.", vs: 'Others: Monthly PDF reports' },
 { icon: '', title: 'Unlimited Iterations', desc: 'Business changes. Your automations evolve with you. All plans include unlimited updates, additions, and optimisations to live workflows.', vs: 'Others: Charge per change request' },
 ];

 const steps = [
 { n: '01', icon: '', title: 'Free Automation Audit', desc: 'We analyze your current workflows and identify every manual bottleneck — at no cost. You get a full report with ROI estimates for each automation opportunity.' },
 { n: '02', icon: '', title: 'Custom Solution Design', desc: 'Our AI engineers design a tailored automation architecture — no off-the-shelf templates. Every workflow is purpose-built for your specific tech stack and team.' },
 { n: '03', icon: '', title: 'Build & Deploy Fast', desc: 'We ship production-ready systems within 2–4 weeks. Full testing, documentation, and team training included. Zero disruption to your existing operations.' },
 { n: '04', icon: '', title: 'Monitor & Optimise', desc: 'After launch, ARIA (our AI monitoring system) tracks performance 24/7, flags issues, and continuously improves automation accuracy. Your systems get smarter over time.' },
 ];

 const workflowSteps = [
 { icon: '', title: 'Email Arrives', desc: 'Invoice sent to automation inbox' },
 { icon: '', title: 'AI Reads & Extracts', desc: 'Amount, vendor, date extracted instantly' },
 { icon: '', title: 'Auto-Match & Approve', desc: 'Matches PO, approves or flags for review' },
 { icon: '', title: 'Post to QuickBooks', desc: 'Automatically synced to accounting system' },
 ];

 const examples = [
 { icon: '', label: 'Invoice Processing', save: '40 hrs/mo saved', desc: 'Auto-extract data from PDFs, match to POs, approve or flag for review, post to QuickBooks — zero human touch on clean invoices.', metric: '99.2% accuracy' },
 { icon: '', label: 'Customer Support AI', save: '68% resolved automatically', desc: '24/7 chatbot trained on your product docs, handles refunds, order tracking, FAQs. Escalates complex issues to humans with full context.', metric: '+12pt CSAT' },
 { icon: '', label: 'HR Onboarding Bot', save: '12 hrs per hire saved', desc: 'Automates offer letters, IT provisioning, Slack setup, day-1 schedule, and 30-day check-ins. New hires are productive from day one.', metric: '100% on-time' },
 { icon: '', label: 'Sales Pipeline Sync', save: 'Real-time across 5 tools', desc: 'Bi-directional sync between CRM, email, calendar, Slack and reporting dashboards. Sales managers always have live pipeline data.', metric: 'Zero lag' },
 { icon: '', label: 'Contract AI Review', save: '80% faster review', desc: 'AI reads NDAs, SOWs and vendor contracts, flags non-standard clauses, extracts key dates and obligations into a structured summary.', metric: '3 min/contract' },
 { icon: '', label: 'Automated KPI Reports', save: 'Daily CEO briefings in seconds', desc: 'Pulls data from GA4, Stripe, HubSpot, and Jira every morning. Generates and emails a formatted executive report with highlights and alerts.', metric: 'Daily auto' },
 { icon: '', label: 'Algo Trading Bot', save: 'Trades 24/7 — zero emotion', desc: 'Custom algorithmic trading system with AI signal generation, multi-exchange execution, automated risk controls, and real-time P&L dashboards.', metric: 'Live 24/7' },
 { icon: '', label: 'Strategy Backtesting Engine', save: '10 yrs of data in seconds', desc: 'We build backtesting pipelines that run your trading strategies against historical market data, optimise parameters, and generate detailed performance reports.', metric: 'Full analytics' },
 ];

 const pricing = [
 { name: 'Starter', price: 'From $3,500', sub: '/ month', color: '#4f8ef7', features: ['2 automation workflows built & maintained', 'AI chatbot integration (1 channel)', 'Weekly strategy call', 'Email + Slack support', 'Monthly performance report', 'Free onboarding audit included'], cta: 'Get Started', outline: true },
 { name: 'Professional', price: 'From $7,500', sub: '/ month', color: '#7c3aed', badge: 'Most Popular', features: ['Up to 5 automation workflows', 'Custom AI chatbot (trained on your data)', 'Dedicated AI engineer', 'Priority same-day support', 'Weekly strategy calls', 'Live analytics dashboard', 'API integrations (unlimited)'], cta: 'Start Automating', outline: false },
 { name: 'Scale', price: 'From $15,000', sub: '/ month', color: '#4f8ef7', features: ['Unlimited automation workflows', 'Custom AI SaaS product build', '2 engineers + project manager', 'Trading strategy automation included', '4-hr SLA response time', 'Quarterly exec strategy sessions', 'White-label chatbot suite'], cta: 'Talk to Us', outline: false },
 { name: 'Enterprise', price: 'Custom', sub: 'tailored to you', color: '#7c3aed', features: ['Everything in Scale', 'Dedicated engineering team (4+)', 'On-site workshops & training', 'Full AI transformation roadmap', 'Custom NDA & SLA contracts', '24 / 7 priority hotline', 'Co-development partnerships'], cta: 'Contact Us', outline: true },
 ];

 const industries = ['E-Commerce','HealthTech','FinTech & Insurance','Algorithmic Trading','Crypto & DeFi','Legal & Compliance','Real Estate','HR & Recruiting','Logistics & Supply Chain','SaaS & Tech','Hedge Funds & Prop Trading','Forex & Futures','Social Media Marketing'];

 const industryData = {
 'E-Commerce': { icon: '', color: '#f59e0b', desc: 'AI-powered order management, inventory sync, abandoned cart recovery, product recommendation engines, and automated customer support for online stores.', products: ['ViralVolt — viral product post generator', 'Order workflow automation', 'AI customer support bot', 'Smart inventory sync'] },
 'HealthTech': { icon: '', color: '#10b981', desc: 'Patient intake automation, appointment scheduling bots, HIPAA-compliant document processing, insurance claim automation, and EHR data pipelines.', products: ['Patient onboarding automation', 'Appointment scheduling AI', 'Insurance claim processor', 'Medical document AI'] },
 'FinTech & Insurance': { icon: '', color: '#4f8ef7', desc: 'KYC/AML automation, fraud detection pipelines, claims processing AI, policy document review, automated compliance reporting, and client onboarding workflows.', products: ['Investiy — stock trading intelligence', 'KYC onboarding automation', 'Claims processing AI', 'Fraud detection pipeline', 'Compliance report bot'] },
 'Algorithmic Trading': { icon: '', color: '#7c3aed', desc: 'Custom algo trading bots with AI signal generation, multi-exchange execution (Binance, Kraken, Interactive Brokers), backtesting engines, and live risk management dashboards.', products: ['Investiy — A-F setup scanner + risk sizing', 'Custom algo trading bot', 'AI signal generator', 'Backtesting engine', 'Risk management AI'] },
 'Crypto & DeFi': { icon: '₿', color: '#f59e0b', desc: 'DeFi yield optimisation bots, on-chain analytics pipelines, NFT market automation, wallet monitoring, and cross-exchange arbitrage systems.', products: ['DeFi yield bot', 'On-chain analytics AI', 'Arbitrage system', 'Wallet monitoring bot'] },
 'Legal & Compliance': { icon: '', color: '#06b6d4', desc: 'AI contract review (NDA, SOW, vendor agreements), clause extraction, compliance monitoring, automated legal research, and document generation workflows.', products: ['AI contract reviewer', 'Clause extraction AI', 'Compliance monitor', 'Legal doc generator'] },
 'Real Estate': { icon: '', color: '#10b981', desc: 'Lead qualification bots, automated property listing syndication, CRM integration, tenant onboarding workflows, and AI-powered market analysis reports.', products: ['Lead qualification bot', 'Listing automation', 'Tenant onboarding AI', 'Market analysis bot'] },
 'HR & Recruiting': { icon: '', color: '#7c3aed', desc: 'AI resume screening, automated candidate outreach, onboarding workflow automation, offer letter generation, and HR analytics dashboards with live headcount data.', products: ['AI resume screener', 'Candidate outreach bot', 'Onboarding automation', 'HR analytics dashboard'] },
 'Logistics & Supply Chain': { icon: '', color: '#4f8ef7', desc: 'Shipment tracking automation, supplier communication bots, demand forecasting AI, warehouse management integration, and customs documentation processors.', products: ['Shipment tracking AI', 'Supplier comms bot', 'Demand forecasting AI', 'Customs doc processor'] },
 'SaaS & Tech': { icon: '', color: '#06b6d4', desc: 'Full AI SaaS product development, feature automation pipelines, user onboarding flows, churn prediction AI, support ticket automation, and growth analytics systems.', products: ['ViralVolt — social media AI', 'AI SaaS development', 'Churn prediction AI', 'Support ticket automation'] },
 'Hedge Funds & Prop Trading': { icon: '', color: '#f59e0b', desc: 'Institutional-grade trading infrastructure — strategy backtesting, multi-asset execution, portfolio risk AI, factor model automation, and real-time P&L dashboards.', products: ['Investiy — explainable setup analysis', 'Multi-asset trading bot', 'Portfolio risk AI', 'Factor model automation', 'Real-time P&L dashboard'] },
 'Forex & Futures': { icon: '', color: '#10b981', desc: 'Forex algo bots with technical & fundamental signal layers, futures contract automation, broker API integration (MT4/MT5, OANDA, IBKR), and automated trade journaling.', products: ['Investiy — trade planner + journal (equities)', 'Forex algo bot', 'Futures automation', 'MT4/MT5 integration', 'Trade journal AI'] },
 'Social Media Marketing': { icon: '', color: '#ec4899', desc: 'Full AI social media marketing automation — viral post generation, hashtag strategy, content calendars, brand voice training, competitor monitoring, and growth analytics across all major platforms.', products: ['ViralVolt — AI viral content creator', 'Hashtag & reach optimisation AI', 'Content calendar automation', 'Competitor trend monitoring', 'Growth analytics dashboard'] },
 };

 const testimonials = [
 { quote: 'Gantima automated our entire invoice approval process. What used to take our finance team 3 days per week now runs overnight with zero errors. The ROI was visible within 30 days.', name: 'Sarah K.', role: 'CFO, MedSupply Co.', metric: '3 days → overnight', metricLabel: 'Processing Time' },
 { quote: "Their AI chatbot handles 70% of our customer support tickets without human intervention. Our CSAT score actually went up 12 points because response times dropped from hours to seconds.", name: 'Marcus T.', role: 'VP Customer Experience, RetailFlow', metric: '70% resolved by AI', metricLabel: 'Support Automation' },
 { quote: 'We hired Gantima to build our AI-powered SaaS product. They delivered a full working application in 6 weeks — React frontend, Node backend, ML pipeline, all deployed on AWS. Phenomenal team.', name: 'Priya N.', role: 'CEO, AutomateHR', metric: '6 weeks to launch', metricLabel: 'Time to Market' },
 { quote: "Gantima built our crypto algo trading system from scratch — signal generation, backtesting, live execution on Binance, and a risk management layer. It's been running profitably for 8 months.", name: 'James O.', role: 'Head of Quant, TradeFusion Capital', metric: '8 months live', metricLabel: 'Zero downtime' },
 ];

 const team = [
 { initials: 'AC', gradient: 'linear-gradient(135deg,#4f8ef7 0%,#7c3aed 100%)', glow: 'rgba(79,142,247,0.35)', name: 'Alex Chen', role: 'Lead AI Engineer', desc: 'GPT-4 & LangChain expert. Built 150+ automations.' },
 { initials: 'SM', gradient: 'linear-gradient(135deg,#a48bff 0%,#ec4899 100%)', glow: 'rgba(164,139,255,0.35)', name: 'Sarah Martinez', role: 'Automation Architect', desc: 'Enterprise integration specialist. 10+ years ops.' },
 { initials: 'JP', gradient: 'linear-gradient(135deg,#10b981 0%,#4f8ef7 100%)', glow: 'rgba(16,185,129,0.35)', name: 'James Park', role: 'Trading Systems Dev', desc: 'Algo trading & backtesting. Crypto & forex expert.' },
 { initials: 'ES', gradient: 'linear-gradient(135deg,#f59e0b 0%,#ec4899 100%)', glow: 'rgba(245,158,11,0.35)', name: 'Emma Singh', role: 'Strategy Lead', desc: 'ROI modeling & process optimization specialist.' },
 ];

 const monitors = [
 { title: 'Invoice Processing Bot', stats: [{ label: "Today's Processed", value: '247' }, { label: 'Time Saved Today', value: '34.2 hrs' }, { label: 'Success Rate', value: '99.8%', green: true }, { label: 'Uptime This Month', value: '99.94%', green: true }], bar1: 92, bar2: 99.8 },
 { title: 'Customer Support AI', stats: [{ label: 'Tickets Resolved', value: '1,284' }, { label: 'Avg Response Time', value: '12 sec' }, { label: 'Customer Satisfaction', value: '4.8', green: true }, { label: 'Cost Saved YTD', value: '$84,200', green: true }], bar1: 70, bar2: 96 },
 { title: 'Sales Pipeline Sync', stats: [{ label: 'Records Synced', value: '9,847' }, { label: 'Sync Frequency', value: 'Real-time' }, { label: 'Data Accuracy', value: '100%', green: true }, { label: 'Last Sync', value: '2 sec ago' }] },
 { title: 'Trading Bot - BTC/USD', stats: [{ label: 'Trades Executed', value: '247' }, { label: 'Win Rate', value: '62.3%', green: true }, { label: '30-Day P&L', value: '+$12,840', green: true }, { label: 'Risk Level', value: 'Medium', amber: true }] },
 ];

 const faqs = [
 { q: 'How long does it take to deploy an automation?', a: "Most automation workflows are live within 2–4 weeks. Complex SaaS builds or multi-system integrations may take 6–10 weeks. Every project starts with a free audit so we can give you an accurate timeline before any commitment." },
 { q: 'Do we need to change our existing tools or software?', a: "No. We build around your existing stack. Whether you're on HubSpot, Salesforce, QuickBooks, Slack, or any combination of tools — we integrate with what you already use. No migration required." },
 { q: 'What if the automation breaks or needs updating?', a: "All plans include ongoing maintenance. ARIA, our AI monitoring system, watches your automations 24/7 and flags issues before they impact your business. Updates are included in your monthly retainer." },
 { q: 'How is Gantima different from just using Zapier ourselves?', a: "Zapier handles simple linear workflows — one trigger, one action. We build sophisticated multi-step systems with conditional logic, AI decision-making, error handling, and cross-platform orchestration that no-code tools can't replicate." },
 { q: 'Is there a minimum contract length?', a: "We ask for a 3-month initial engagement so we have time to build, test, and optimise your automations properly. After that, it's month-to-month. Most clients stay because the ROI speaks for itself." },
 { q: 'Can you automate trading strategies? Is that legal?', a: "Yes — algorithmic trading automation is completely legal for personal and institutional use. We build the technical infrastructure: bots, backtesting engines, and execution pipelines. You own the strategy and control all capital decisions." },
 ];

 const chartBars = [
 { label: 'Invoices', target: 85, color: 'linear-gradient(90deg,#4f8ef7,#6d9fff)', hrs: '34h' },
 { label: 'Support', target: 65, color: 'linear-gradient(90deg,#7c3aed,#a78bfa)', hrs: '26h' },
 { label: 'HR', target: 45, color: 'linear-gradient(90deg,#06b6d4,#67e8f9)', hrs: '18h' },
 { label: 'Sales', target: 55, color: 'linear-gradient(90deg,#10b981,#6ee7b7)', hrs: '22h' },
 { label: 'Reports', target: 35, color: 'linear-gradient(90deg,#f59e0b,#fcd34d)', hrs: '14h' },
 ];

 const faqSchema = {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 "mainEntity": faqs.map(f => ({
 "@type": "Question",
 "name": f.q,
 "acceptedAnswer": { "@type": "Answer", "text": f.a }
 }))
 };

 return (
 <div className="mkt-root"><Helmet><title>Gantima | #1 AI Automation Agency — Chatbots, Workflow & Trading Bots</title><meta name="description" content="Gantima is a top-rated AI automation agency. We build custom AI chatbots, workflow automation systems, algo trading bots, invoice processing automation, and AI SaaS products. Save 500+ hours/month. Book a free consultation today." /><meta name="keywords" content="AI automation agency, workflow automation company, AI chatbot development company, algorithmic trading bot development, business process automation, RPA company, AI SaaS development, automation agency UK, automation agency USA, n8n automation expert, Make.com automation, Zapier automation expert, invoice automation, HR onboarding automation, CRM automation, sales automation, AI software development company, gantima, gantima software, best AI automation agency 2025 2026" /><link rel="canonical" href="https://www.gantima.com/" /><meta property="og:title" content="Gantima | #1 AI Automation Agency — Chatbots, Workflows & Trading Bots" /><meta property="og:description" content="We build AI automation systems that replace thousands of hours of manual work. Custom chatbots, workflow automation, algo trading bots. Book a free consultation." /><meta property="og:url" content="https://www.gantima.com/" /><meta property="og:type" content="website" /><script type="application/ld+json">{JSON.stringify(faqSchema)}</script><script type="application/ld+json">{JSON.stringify({
 "@context": "https://schema.org",
 "@type": "LocalBusiness",
 "name": "Gantima Software",
 "description": "AI automation agency specializing in workflow automation, AI chatbots, algorithmic trading bots, and AI SaaS product development.",
 "url": "https://www.gantima.com",
 "email": "support@gantima.com",
 "priceRange": "$$$",
 "areaServed": "Worldwide",
 "serviceArea": { "@type": "GeoCircle", "geoMidpoint": { "@type": "GeoCoordinates" } },
 "hasOfferCatalog": {
 "@type": "OfferCatalog",
 "name": "AI Automation Services",
 "itemListElement": [
 {"@type":"Offer","itemOffered":{"@type":"Service","name":"AI Chatbot Development","description":"Custom AI chatbots trained on your data for customer support, lead generation, and internal knowledge bases"}},
 {"@type":"Offer","itemOffered":{"@type":"Service","name":"Workflow Automation","description":"End-to-end business process automation using n8n, Make, Zapier and custom code"}},
 {"@type":"Offer","itemOffered":{"@type":"Service","name":"Algorithmic Trading Bot","description":"Custom algo trading systems with AI signal generation, backtesting, and live execution"}},
 {"@type":"Offer","itemOffered":{"@type":"Service","name":"Invoice Processing Automation","description":"Auto-extract, match, approve and post invoices with 99%+ accuracy"}},
 {"@type":"Offer","itemOffered":{"@type":"Service","name":"AI SaaS Development","description":"Custom AI-powered software products built for your market"}}
 ]
 }
 })}</script></Helmet>

 {/* ═══ MOBILE RESPONSIVE FIXES for Tesla-style sections ═══════════════ */}
 <style>{`
 /* Prevent horizontal overflow globally on mobile */
 html, body { overflow-x: hidden; max-width: 100vw; }

 @media (max-width: 900px) {
 /* Nav: hide the CTA button so it doesn't collide with the logo — hamburger opens the menu with its own CTA */
 .mkt-nav .mkt-nav-actions .mkt-btn-primary { display: none !important; }
 .mkt-nav .mkt-nav-actions { gap: 0 !important; }
 /* Shrink logo so it fits alongside the hamburger */
 .mkt-nav-brand img { height: 34px !important; }

 /* Kill perspective transforms on mobile — they cause horizontal scroll */
 .mkt-root section [style*="perspective"] { transform: none !important; }

 /* Tesla-style hero: shrink headline, adjust padding, hide subtle indicator */
 #home h1 { font-size: clamp(2.4rem, 10vw, 3.6rem) !important; letter-spacing: -1.5px !important; }
 #home p { font-size: 1rem !important; padding: 0 8px; }
 #home { padding: 100px 20px 140px !important; min-height: auto !important; }

 /* Spec row wraps + smaller on mobile */
 #home > div[style*="bottom: 40px"], #home > div[style*="bottom:40px"] {
 position: static !important;
 transform: none !important;
 margin-top: 40px !important;
 gap: 20px !important;
 padding: 0 !important;
 }
 #home > div[style*="Scroll to explore"] { display: none !important; }
 }

 @media (max-width: 700px) {
 /* Product sections: reduce padding, less vh */
 .mkt-root section[style*="minHeight:'92vh'"], .mkt-root section[style*='minHeight:"92vh"'] {
 min-height: auto !important;
 padding: 60px 20px !important;
 }

 /* Grid gaps shrink on mobile */
 .mkt-root section > div[style*="grid"] { gap: 32px !important; }

 /* Big headlines shrink */
 .mkt-root section h2 { font-size: clamp(2rem, 8vw, 2.6rem) !important; letter-spacing: -1px !important; }

 /* Buttons: full-width so they don't wrap ugly */
 .mkt-root section a[style*="TRY FREE"], .mkt-root section button[style*="uppercase"] {
 width: 100%;
 min-width: 0;
 }

 /* Sticky bottom nav: stack vertically on very small screens */
 .mkt-root > div[style*="position:fixed"][style*="bottom:0"], .mkt-root > div[style*="position: fixed"][style*="bottom: 0"] {
 padding: 10px 16px !important;
 gap: 10px !important;
 }
 .mkt-root > div[style*="position:fixed"][style*="bottom:0"] img,
 .mkt-root > div[style*="position: fixed"][style*="bottom: 0"] img {
 height: 22px !important;
 }
 .mkt-root > div[style*="position:fixed"][style*="bottom:0"] a,
 .mkt-root > div[style*="position:fixed"][style*="bottom:0"] button {
 padding: 8px 14px !important;
 font-size: 0.72rem !important;
 }

 /* Reserve space at bottom of page so sticky nav doesn't cover footer */
 .mkt-footer { padding-bottom: 100px !important; }

 /* Mockup cards: shrink padding, ensure they fit */
 .mkt-root section div[style*="borderRadius:'20px'"], .mkt-root section div[style*="borderRadius: '20px'"] {
 padding: 20px !important;
 }
 }

 @media (max-width: 480px) {
 /* Ultra-narrow: fine-tune the huge hero */
 #home h1 { font-size: 2rem !important; letter-spacing: -1px !important; }
 #home p { font-size: 0.95rem !important; }
 #home > div[style*="static"] { flex-direction: column !important; gap: 14px !important; }

 /* Sticky nav: hide the "Ready to automate?" tagline */
 .mkt-root > div[style*="position: fixed"][style*="bottom: 0"] span[style*="Ready"] { display: none; }
 }
 `}</style>

 {/* ── Navigation ─────────────────────────────── */}
 <nav className="mkt-nav"><a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'10px', textDecoration:'none' }}><img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" style={{ height:'44px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 14px rgba(79,142,247,0.5))' }} /></a><ul className="mkt-nav-links"><li><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>Services</a></li><li className="mkt-nav-dropdown"><span className="mkt-nav-dropdown-trigger">Products ▾</span><div className="mkt-nav-dropdown-menu"><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured"><div><div className="mkt-nav-dropdown-name">ViralVolt.ai</div><div className="mkt-nav-dropdown-desc">AI Social Media Marketing Tool</div></div></a><a href="https://investiy.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured"><div><div className="mkt-nav-dropdown-name">Investiy.ai</div><div className="mkt-nav-dropdown-desc">Trading Intelligence for Stock Traders</div></div></a><div className="mkt-nav-dropdown-label">Solutions by Industry</div><div className="mkt-nav-dropdown-grid"><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>E-Commerce</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HealthTech</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>FinTech & Insurance</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Algorithmic Trading</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Crypto & DeFi</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Legal & Compliance</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Real Estate</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HR & Recruiting</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Logistics & Supply Chain</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>SaaS & Tech</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Hedge Funds & Prop Trading</a><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Forex & Futures</a><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Social Media Marketing</a><a href="https://investiy.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Stock Trading Analysis</a></div></div></li><li><a href="#why" onClick={e => { e.preventDefault(); navTo('why'); }}>Why Us</a></li><li><a href="#how" onClick={e => { e.preventDefault(); navTo('how'); }}>How It Works</a></li><li><a href="#examples" onClick={e => { e.preventDefault(); navTo('examples'); }}>Showcase</a></li><li><a href="#pricing" onClick={e => { e.preventDefault(); navTo('pricing'); }}>Pricing</a></li><li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li></ul><div className="mkt-nav-actions"><button className="mkt-btn mkt-btn-primary mkt-btn-glow" onClick={() => setModalOpen(true)}>Book Free Consultation</button><button className="mkt-hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu"><span /><span /><span /></button></div>
 {menuOpen && (
 <div className="mkt-mobile-menu"><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>Services</a><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer"> ViralVolt.ai</a><a href="https://investiy.ai/" target="_blank" rel="noopener noreferrer"> Investiy.ai</a><a href="#how" onClick={e => { e.preventDefault(); navTo('how'); }}>How It Works</a><a href="#examples" onClick={e => { e.preventDefault(); navTo('examples'); }}>Showcase</a><a href="#pricing" onClick={e => { e.preventDefault(); navTo('pricing'); }}>Pricing</a><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); setMenuOpen(false); }}>Careers</a><button className="mkt-btn mkt-btn-primary" onClick={() => { setModalOpen(true); setMenuOpen(false); }}>Book Free Consultation</button></div>
 )}
 </nav>

 {/* ═══ TESLA-STYLE CINEMATIC HERO — full-viewport centered statement ═════ */}
 <section id="home" style={{
 minHeight:'100vh',
 background:'radial-gradient(circle at 50% 30%, rgba(79,142,247,0.18), transparent 55%), radial-gradient(circle at 20% 80%, rgba(124,58,237,0.14), transparent 50%), radial-gradient(circle at 80% 70%, rgba(236,72,153,0.10), transparent 50%), #030510',
 display:'flex',
 flexDirection:'column',
 alignItems:'center',
 justifyContent:'center',
 padding:'120px 5% 80px',
 position:'relative',
 textAlign:'center',
 overflow:'hidden',
 }}>
 {/* Subtle floating orbs for depth */}
 <div style={{ position:'absolute', width:'480px', height:'480px', borderRadius:'50%', background:'radial-gradient(circle, rgba(79,142,247,0.18), transparent 70%)', filter:'blur(80px)', top:'10%', left:'-8%', pointerEvents:'none' }} /><div style={{ position:'absolute', width:'520px', height:'520px', borderRadius:'50%', background:'radial-gradient(circle, rgba(124,58,237,0.15), transparent 70%)', filter:'blur(80px)', bottom:'5%', right:'-10%', pointerEvents:'none' }} />

 {/* Award pill (small, subtle — Tesla puts these above the fold) */}
 <div style={{
 display:'inline-flex', alignItems:'center', gap:'8px',
 padding:'7px 16px',
 background:'rgba(240,244,255,0.06)',
 border:'1px solid rgba(240,244,255,0.15)',
 borderRadius:'999px',
 fontSize:'.78rem', fontWeight:600, color:'#c0c8dc',
 letterSpacing:'0.3px',
 marginBottom:'40px',
 backdropFilter:'blur(10px)',
 zIndex:2,
 }}><span style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#5bd4b8', boxShadow:'0 0 12px #5bd4b8' }} />
 Now booking Q4 2026 automation projects
 </div>

 {/* MASSIVE Tesla headline */}
 <h1 style={{
 fontSize:'clamp(3rem, 8vw, 6.5rem)',
 fontWeight:200,
 color:'#f0f4ff',
 lineHeight:1.02,
 letterSpacing:'-3px',
 maxWidth:'1100px',
 margin:'0 auto 28px',
 zIndex:2,
 }}>
 Automate the work<br/><span style={{ background:'linear-gradient(135deg, #7eb0ff, #a48bff, #ec4899)', WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent', fontWeight:400 }}>machines can do.</span></h1>

 {/* Thin, elegant subtitle */}
 <p style={{
 fontSize:'clamp(1.05rem, 1.8vw, 1.45rem)',
 color:'#a8b3cf',
 lineHeight:1.5,
 fontWeight:300,
 maxWidth:'720px',
 margin:'0 auto 52px',
 zIndex:2,
 }}>
 Gantima builds AI systems that eliminate manual work &mdash; custom automation, AI chatbots, algorithmic trading bots, and our own SaaS products (Viral Volt, viralvolt.ai). Deployed in 2 weeks.
 </p>

 {/* Tesla-style pill CTAs */}
 <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', justifyContent:'center', zIndex:2 }}><button onClick={()=>setModalOpen(true)} style={{
 padding:'18px 48px',
 background:'#f0f4ff',
 color:'#030510',
 fontWeight:700,
 fontSize:'.95rem',
 letterSpacing:'1.5px',
 textTransform:'uppercase',
 borderRadius:'6px',
 border:'none',
 cursor:'pointer',
 fontFamily:'inherit',
 transition:'transform .15s',
 }} onMouseOver={e=>e.currentTarget.style.transform='translateY(-2px)'} onMouseOut={e=>e.currentTarget.style.transform='translateY(0)'}>Get Started</button><button onClick={()=>navTo('examples')} style={{
 padding:'18px 48px',
 background:'transparent',
 color:'#f0f4ff',
 fontWeight:700,
 fontSize:'.95rem',
 letterSpacing:'1.5px',
 textTransform:'uppercase',
 borderRadius:'6px',
 border:'1px solid rgba(240,244,255,0.35)',
 cursor:'pointer',
 fontFamily:'inherit',
 transition:'all .15s',
 }} onMouseOver={e=>{e.currentTarget.style.background='rgba(240,244,255,0.06)';e.currentTarget.style.borderColor='rgba(240,244,255,0.6)';}} onMouseOut={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='rgba(240,244,255,0.35)';}}>Learn More</button></div>

 {/* Small anchor stats — Tesla-style spec row (KM range | 0-100 | max speed etc.) */}
 <div style={{
 position:'absolute',
 bottom:'40px',
 left:'50%',
 transform:'translateX(-50%)',
 display:'flex',
 gap:'48px',
 zIndex:2,
 flexWrap:'wrap',
 justifyContent:'center',
 padding:'0 20px',
 }}>
 {[
 { val:'500+', lbl:'Automations shipped' },
 { val:'$47M', lbl:'Client value generated' },
 { val:'99%', lbl:'Client retention' },
 { val:'2 weeks', lbl:'Avg deployment' },
 ].map((s,i)=>(
 <div key={i} style={{ textAlign:'center' }}><div style={{ fontSize:'1.5rem', fontWeight:200, color:'#f0f4ff', letterSpacing:'-0.5px' }}>{s.val}</div><div style={{ fontSize:'.72rem', color:'#7a85a0', letterSpacing:'1.2px', textTransform:'uppercase', marginTop:'4px' }}>{s.lbl}</div></div>
 ))}
 </div>

 {/* Subtle scroll indicator (Tesla move) */}
 <div style={{ position:'absolute', bottom:'12px', left:'50%', transform:'translateX(-50%)', color:'#7a85a0', fontSize:'.7rem', letterSpacing:'2px', textTransform:'uppercase', animation:'pulse 2s ease-in-out infinite' }}>
 ↓ Scroll to explore
 </div></section>

 {/* ═══ APPLE-STYLE PROMO VIDEO ══════════════════════════════════════════ */}
 <section style={{
 position:'relative',
 background:'radial-gradient(ellipse at center, #0a1428 0%, #000 70%)',
 padding:'100px 5% 120px',
 overflow:'hidden',
 borderTop:'1px solid rgba(79,142,247,0.1)',
 }}>
 <div style={{ position:'absolute', top:'20%', left:'20%', width:'400px', height:'400px', background:'radial-gradient(circle, rgba(79,142,247,0.15), transparent 70%)', filter:'blur(70px)', pointerEvents:'none' }} />
 <div style={{ position:'absolute', bottom:'10%', right:'15%', width:'500px', height:'500px', background:'radial-gradient(circle, rgba(164,139,255,0.12), transparent 70%)', filter:'blur(90px)', pointerEvents:'none' }} />

 <div style={{ maxWidth:'1100px', margin:'0 auto', position:'relative', zIndex:2 }}>
 {/* Header */}
 <div className="anim anim-up" style={{ textAlign:'center', marginBottom:'50px' }}>
 <div style={{
 display:'inline-flex', alignItems:'center', gap:'8px',
 padding:'6px 14px', background:'rgba(79,142,247,0.12)',
 border:'1px solid rgba(79,142,247,0.3)', borderRadius:'999px',
 marginBottom:'22px', fontSize:'.72rem', letterSpacing:'2px', textTransform:'uppercase', color:'#a48bff', fontWeight:800,
 }}>
 <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#ec4899', boxShadow:'0 0 12px #ec4899', animation:'pulse 2s ease-in-out infinite' }} />
 Watch · 10s
 </div>
 <h2 style={{
 fontSize:'clamp(2rem, 4.5vw, 3.6rem)',
 fontWeight:100,
 color:'#f0f4ff',
 lineHeight:1.05,
 letterSpacing:'-2px',
 margin:'0 0 16px'
 }}>
 See Gantima <span className="mkt-gradient-text">in action.</span>
 </h2>
 <p style={{ fontSize:'clamp(1rem, 1.5vw, 1.2rem)', color:'#8a94b0', maxWidth:'620px', margin:'0 auto', fontWeight:300, lineHeight:1.55 }}>
 A quick look at what we build and why clients trust us with their most critical automation.
 </p>
 </div>

 {/* Video player */}
 <div className="anim anim-scale" style={{ position:'relative' }}>
 <div style={{
 position:'relative',
 borderRadius:'24px',
 overflow:'hidden',
 background:'#000',
 boxShadow:'0 40px 100px rgba(79,142,247,0.25), 0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(79,142,247,0.15)',
 aspectRatio:'16 / 9',
 cursor:'pointer',
 }} onClick={togglePromoPlay}>
 <video
 ref={promoVideoRef}
 autoPlay
 loop
 muted
 playsInline
 preload="auto"
 style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', background:'#000' }}
 onPlay={() => setPromoPlaying(true)}
 onPause={() => setPromoPlaying(false)}
 onError={() => setPromoError(true)}
 onLoadedData={() => setPromoError(false)}
 >
 <source src="https://gantima-media-public.s3.us-east-1.amazonaws.com/gantima-promo.mp4" type="video/mp4" />
 </video>

 {promoError && (
 <div style={{
 position:'absolute', inset:0, display:'flex', flexDirection:'column',
 alignItems:'center', justifyContent:'center', gap:'12px', textAlign:'center', padding:'24px',
 background:'linear-gradient(135deg, rgba(79,142,247,0.12), rgba(164,139,255,0.08))',
 }}>
 <div style={{ fontSize:'2rem' }}>⏳</div>
 <div style={{ color:'#f0f4ff', fontWeight:600, fontSize:'1.05rem' }}>Video is warming up</div>
 <div style={{ color:'#8a94b0', fontSize:'.85rem' }}>Refresh in a moment — Amplify is finishing the deploy.</div>
 </div>
 )}

 {/* Bottom gradient overlay for controls */}
 <div style={{
 position:'absolute', bottom:0, left:0, right:0, height:'80px',
 background:'linear-gradient(180deg, transparent, rgba(0,0,0,0.7))',
 pointerEvents:'none',
 }} />

 {/* Play/Pause overlay when paused */}
 {!promoPlaying && (
 <div style={{
 position:'absolute', inset:0,
 display:'flex', alignItems:'center', justifyContent:'center',
 background:'rgba(0,0,0,0.3)', backdropFilter:'blur(4px)',
 transition:'all .3s',
 }}>
 <div style={{
 width:'88px', height:'88px', borderRadius:'50%',
 background:'linear-gradient(135deg, #4f8ef7, #a48bff)',
 display:'flex', alignItems:'center', justifyContent:'center',
 boxShadow:'0 20px 60px rgba(79,142,247,0.5)',
 }}>
 <svg width="34" height="34" viewBox="0 0 24 24" fill="#fff">
 <polygon points="6 4 20 12 6 20" />
 </svg>
 </div>
 </div>
 )}

 {/* Mute/unmute button — bottom right */}
 <button
 onClick={(e) => { e.stopPropagation(); togglePromoMute(); }}
 aria-label={promoMuted ? 'Unmute video' : 'Mute video'}
 style={{
 position:'absolute', bottom:'20px', right:'20px',
 width:'48px', height:'48px', borderRadius:'50%',
 background:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.2)',
 backdropFilter:'blur(12px)',
 display:'flex', alignItems:'center', justifyContent:'center',
 cursor:'pointer', color:'#fff', transition:'all .2s',
 }}
 onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,142,247,0.8)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
 onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.6)'; e.currentTarget.style.transform = 'scale(1)'; }}
 >
 {promoMuted ? (
 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
 </svg>
 ) : (
 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
 </svg>
 )}
 </button>

 {/* "Tap to unmute" hint — only when muted + playing */}
 {promoMuted && promoPlaying && (
 <div style={{
 position:'absolute', bottom:'22px', left:'20px',
 padding:'6px 12px', background:'rgba(0,0,0,0.6)',
 backdropFilter:'blur(10px)',
 border:'1px solid rgba(255,255,255,0.15)',
 borderRadius:'999px',
 fontSize:'.72rem', letterSpacing:'.5px', color:'#f0f4ff', fontWeight:600,
 pointerEvents:'none',
 }}>
 Tap for sound
 </div>
 )}
 </div>
 </div>

 {/* CTA below video */}
 <div className="anim anim-up" style={{ textAlign:'center', marginTop:'44px' }}>
 <button onClick={() => setModalOpen(true)} style={{
 padding:'16px 40px', background:'#f0f4ff', color:'#050810',
 fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase',
 borderRadius:'6px', border:'none', cursor:'pointer', fontFamily:'inherit',
 }}>Book Your Free Consultation</button>
 </div>
 </div>
 </section>

 {/* ── TRUST BAR ─── Client logos + security badges (enterprise credibility) ── */}
 <section style={{ background: '#020817', padding: '48px 5% 32px', borderBottom: '1px solid rgba(79,142,247,0.08)' }}><div className="mkt-container" style={{ textAlign: 'center' }}><p style={{ fontSize: '0.78em', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#7a85a0', marginBottom: '24px', fontWeight: '700' }}>
 Trusted by growing teams across 13 industries
 </p>
 {/* Logo strip — placeholder client names, fill in real ones as you land them */}
 <div style={{ display: 'flex', gap: '48px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginBottom: '32px', opacity: 0.65 }}>
 {['MedSupply Co.', 'RetailFlow', 'AutomateHR', 'TradeFusion Capital', 'Viral Volt'].map(name => (
 <span key={name} style={{ fontSize: '1.05em', fontWeight: '800', color: '#c0c8dc', letterSpacing: '0.5px' }}>{name}</span>
 ))}
 </div>
 {/* Security & compliance badges — signal enterprise-grade */}
 <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', paddingTop: '24px', borderTop: '1px solid rgba(79,142,247,0.08)' }}>
 {[
 { icon: '', label: 'SOC 2 practices' },
 { icon: '', label: 'GDPR + CCPA compliant' },
 { icon: '', label: '99.94% uptime SLA' },
 { icon: '', label: 'AES-256 encryption' },
 { icon: '', label: 'Custom NDAs available' },
 { icon: '', label: '2-week delivery guaranteed' },
 ].map(b => (
 <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: 'rgba(79,142,247,0.05)', border: '1px solid rgba(79,142,247,0.12)', borderRadius: '999px', fontSize: '0.82em', color: '#c0c8dc' }}><span>{b.icon}</span><span style={{ fontWeight: '600' }}>{b.label}</span></div>
 ))}
 </div></div></section>

 {/* ── PARTNER / ENTERPRISE CALLOUT ─── Direct pitch for high-value clients ── */}
 <section style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.06), rgba(124,58,237,0.04))', padding: '52px 5%', borderBottom: '1px solid rgba(79,142,247,0.1)' }}><div className="mkt-container" style={{ maxWidth: '1100px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
 {/* Enterprise callout */}
 <div style={{ padding: '32px 28px', background: 'rgba(2,8,23,0.6)', border: '1px solid rgba(79,142,247,0.25)', borderRadius: '18px' }}><div style={{ fontSize: '0.72em', letterSpacing: '2px', textTransform: 'uppercase', color: '#4f8ef7', fontWeight: '800', marginBottom: '10px' }}> For Enterprises</div><h3 style={{ fontSize: '1.4em', fontWeight: '900', color: '#eef2ff', margin: '0 0 10px', letterSpacing: '-0.5px' }}>Building at scale? We speak your language.</h3><p style={{ color: '#c0c8dc', fontSize: '0.94em', lineHeight: 1.65, marginBottom: '18px' }}>Dedicated engineering teams, custom SLAs, SOC 2 practices, on-site workshops. From $25,000/mo. We've shipped $47M+ in client value.</p><button className="mkt-btn mkt-btn-primary" onClick={() => setModalOpen(true)}>Talk to Enterprise Sales →</button></div>
 {/* Partner callout */}
 <div style={{ padding: '32px 28px', background: 'rgba(2,8,23,0.6)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '18px' }}><div style={{ fontSize: '0.72em', letterSpacing: '2px', textTransform: 'uppercase', color: '#a48bff', fontWeight: '800', marginBottom: '10px' }}> For Partners</div><h3 style={{ fontSize: '1.4em', fontWeight: '900', color: '#eef2ff', margin: '0 0 10px', letterSpacing: '-0.5px' }}>Agencies, integrators, resellers — let's grow together.</h3><p style={{ color: '#c0c8dc', fontSize: '0.94em', lineHeight: 1.65, marginBottom: '18px' }}>Referral commissions up to 20%, white-label agreements, co-marketing, priority engineering. Partner tier for HubSpot, Salesforce, Shopify agencies.</p><button className="mkt-btn mkt-btn-outline" onClick={() => setModalOpen(true)}>Become a Partner →</button></div>
 {/* Investor callout */}
 <div style={{ padding: '32px 28px', background: 'rgba(2,8,23,0.6)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '18px' }}><div style={{ fontSize: '0.72em', letterSpacing: '2px', textTransform: 'uppercase', color: '#10b981', fontWeight: '800', marginBottom: '10px' }}> For Investors</div><h3 style={{ fontSize: '1.4em', fontWeight: '900', color: '#eef2ff', margin: '0 0 10px', letterSpacing: '-0.5px' }}>Building the AI automation category from Series A onwards.</h3><p style={{ color: '#c0c8dc', fontSize: '0.94em', lineHeight: 1.65, marginBottom: '18px' }}>Profitable agency + our own SaaS products (Viral Volt at viralvolt.ai). Meaningful ARR, strong retention, path to $100M+.</p><a href="mailto:investors@gantima.com?subject=Gantima%20investor%20interest" className="mkt-btn mkt-btn-outline" style={{ display: 'inline-block', textDecoration: 'none' }}>Request Deck →</a></div></div></section>

 {/* ═══ TESLA-STYLE FULL-VIEWPORT PRODUCT SHOWCASE — VIRAL VOLT ═══════════ */}
 <section style={{
 minHeight:'92vh',
 background:'radial-gradient(circle at 30% 50%, rgba(126,176,255,0.18), transparent 55%), radial-gradient(circle at 70% 50%, rgba(164,139,255,0.15), transparent 55%), #050810',
 display:'flex',
 alignItems:'center',
 justifyContent:'center',
 padding:'80px 5%',
 position:'relative',
 overflow:'hidden',
 }}><div style={{ maxWidth:'1200px', width:'100%', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:'60px', alignItems:'center' }}>
 {/* Text */}
 <div style={{ zIndex:2 }}><div style={{ fontSize:'0.75em', fontWeight:800, letterSpacing:'3px', textTransform:'uppercase', color:'#7eb0ff', marginBottom:'18px' }}>
 Featured Product
 </div><h2 style={{
 fontSize:'clamp(2.6rem, 5vw, 4rem)',
 fontWeight:200,
 color:'#f0f4ff',
 lineHeight:1.05,
 letterSpacing:'-2px',
 marginBottom:'22px',
 }}>
 Viral Volt
 </h2><p style={{
 fontSize:'clamp(1.05rem, 1.7vw, 1.35rem)',
 color:'#c0c8dc',
 lineHeight:1.55,
 fontWeight:300,
 maxWidth:'520px',
 marginBottom:'40px',
 }}>
 17 AI tools for creators. Viral hooks, hashtag generator, trend radar, AI video, competitor spy, growth forecaster &mdash; across TikTok, Instagram, YouTube, X, and Facebook. Free forever plan.
 </p><div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}><a href="https://viralvolt.ai/auth?mode=signup" target="_blank" rel="noopener noreferrer" style={{
 display:'inline-block',
 padding:'16px 40px',
 background:'#f0f4ff',
 color:'#050810',
 fontWeight:600,
 fontSize:'.95rem',
 letterSpacing:'1px',
 textTransform:'uppercase',
 borderRadius:'6px',
 textDecoration:'none',
 transition:'all .2s',
 }}>Try Free</a><a href="https://viralvolt.ai" target="_blank" rel="noopener noreferrer" style={{
 display:'inline-block',
 padding:'16px 40px',
 background:'transparent',
 color:'#f0f4ff',
 fontWeight:600,
 fontSize:'.95rem',
 letterSpacing:'1px',
 textTransform:'uppercase',
 borderRadius:'6px',
 textDecoration:'none',
 border:'1px solid rgba(240,244,255,0.4)',
 transition:'all .2s',
 }}>Learn More</a></div></div>
 {/* Visual — product mockup card */}
 <div style={{ position:'relative', zIndex:1 }}><div style={{
 background:'linear-gradient(135deg, #161b2e, #0e1220)',
 border:'1px solid rgba(126,176,255,0.25)',
 borderRadius:'20px',
 padding:'32px',
 boxShadow:'0 40px 80px rgba(0,0,0,0.5), 0 0 60px rgba(126,176,255,0.15)',
 transform:'perspective(1200px) rotateY(-4deg) rotateX(2deg)',
 }}><div style={{ display:'flex', gap:'6px', marginBottom:'20px' }}><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#ff5f57' }} /><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#febc2e' }} /><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#28c840' }} /></div><div style={{ fontSize:'0.7rem', color:'#7eb0ff', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'10px' }}> Trend Radar &middot; TikTok</div><div style={{ fontSize:'1.5rem', fontWeight:800, color:'#f0f4ff', marginBottom:'20px' }}>7 trends climbing right now</div>
 {[
 { name:'#morningroutine', growth:'+340%', hot:true },
 { name:'#dayinmylife POV', growth:'+280%', hot:true },
 { name:'Trending Audio 001', growth:'+520%', hot:false },
 { name:'Storytime hook format', growth:'+190%', hot:false },
 ].map((t,i)=>(
 <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'12px 14px', marginBottom:'8px', background:'rgba(126,176,255,0.05)', border:'1px solid rgba(126,176,255,0.1)', borderRadius:'10px' }}><span style={{ color:'#f0f4ff', fontWeight:600, fontSize:'.88rem' }}>{t.hot ? ' ' : ''}{t.name}</span><span style={{ color:'#5bd4b8', fontWeight:800, fontSize:'.88rem' }}>{t.growth}</span></div>
 ))}
 <div style={{ marginTop:'18px', padding:'14px', background:'linear-gradient(135deg, rgba(126,176,255,0.1), rgba(164,139,255,0.08))', border:'1px solid rgba(126,176,255,0.2)', borderRadius:'10px', fontSize:'.82rem', color:'#c0c8dc' }}><strong style={{ color:'#f0f4ff' }}>AI insight:</strong> Post before 7pm to catch the wave &mdash; peak engagement window.
 </div></div></div></div></section>

 {/* ═══ TESLA-STYLE SECTION — AI AUTOMATION SYSTEMS ═══════════════════════ */}
 <section style={{
 minHeight:'92vh',
 background:'radial-gradient(circle at 70% 40%, rgba(79,142,247,0.16), transparent 55%), radial-gradient(circle at 30% 60%, rgba(16,185,129,0.12), transparent 55%), #050810',
 display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 5%', position:'relative', overflow:'hidden',
 }}><div style={{ maxWidth:'1200px', width:'100%', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:'60px', alignItems:'center' }}>
 {/* Visual on LEFT (alternate for rhythm) */}
 <div style={{ position:'relative', zIndex:1, order:1 }}><div style={{
 background:'linear-gradient(135deg, #0f2040, #050810)',
 border:'1px solid rgba(79,142,247,0.25)',
 borderRadius:'20px',
 padding:'32px',
 boxShadow:'0 40px 80px rgba(0,0,0,0.5), 0 0 60px rgba(79,142,247,0.15)',
 transform:'perspective(1200px) rotateY(4deg) rotateX(2deg)',
 }}><div style={{ display:'flex', gap:'6px', marginBottom:'20px' }}><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#ff5f57' }} /><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#febc2e' }} /><span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#28c840' }} /></div><div style={{ fontSize:'0.7rem', color:'#4f8ef7', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'10px' }}> ARIA · Live Monitoring</div><div style={{ fontSize:'1.5rem', fontWeight:800, color:'#f0f4ff', marginBottom:'20px' }}>Invoice Processing Bot</div>
 {[
 { lbl:'Today\'s Processed', val:'247', color:'#f0f4ff' },
 { lbl:'Time Saved Today', val:'34.2 hrs', color:'#5bd4b8' },
 { lbl:'Success Rate', val:'99.8%', color:'#5bd4b8' },
 { lbl:'Cost Saved Month', val:'$42K', color:'#5bd4b8' },
 ].map((s,i)=>(
 <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'12px 14px', marginBottom:'8px', background:'rgba(79,142,247,0.05)', border:'1px solid rgba(79,142,247,0.1)', borderRadius:'10px' }}><span style={{ color:'#c0c8dc', fontSize:'.88rem' }}>{s.lbl}</span><span style={{ color:s.color, fontWeight:800, fontSize:'.88rem' }}>{s.val}</span></div>
 ))}
 <div style={{ marginTop:'18px', padding:'14px', background:'linear-gradient(135deg, rgba(79,142,247,0.1), rgba(16,185,129,0.08))', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'10px', fontSize:'.82rem', color:'#c0c8dc' }}><strong style={{ color:'#f0f4ff' }}>ARIA:</strong> All automations healthy · Uptime 99.94% this month
 </div></div></div>
 {/* Text on RIGHT */}
 <div style={{ zIndex:2, order:2 }}><div style={{ fontSize:'0.75em', fontWeight:800, letterSpacing:'3px', textTransform:'uppercase', color:'#4f8ef7', marginBottom:'18px' }}> Agency Service</div><h2 style={{ fontSize:'clamp(2.6rem, 5vw, 4rem)', fontWeight:200, color:'#f0f4ff', lineHeight:1.05, letterSpacing:'-2px', marginBottom:'22px' }}>AI Automation Systems</h2><p style={{ fontSize:'clamp(1.05rem, 1.7vw, 1.35rem)', color:'#c0c8dc', lineHeight:1.55, fontWeight:300, maxWidth:'520px', marginBottom:'40px' }}>
 Invoice processing, HR workflows, CRM sync, custom pipelines. We build automations that run overnight while you sleep. Most deploy in 2&ndash;4 weeks. 99.94% uptime monitored 24/7.
 </p><div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}><button onClick={()=>setModalOpen(true)} style={{ padding:'16px 40px', background:'#f0f4ff', color:'#050810', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Book Audit</button><button onClick={()=>navTo('examples')} style={{ padding:'16px 40px', background:'transparent', color:'#f0f4ff', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'1px solid rgba(240,244,255,0.4)', cursor:'pointer', fontFamily:'inherit' }}>See Examples</button></div></div></div></section>

 {/* ═══ TESLA-STYLE SECTION — CUSTOM AI CHATBOTS ═════════════════════════ */}
 <section style={{
 minHeight:'92vh',
 background:'radial-gradient(circle at 30% 40%, rgba(124,58,237,0.16), transparent 55%), radial-gradient(circle at 70% 60%, rgba(236,72,153,0.10), transparent 55%), #050810',
 display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 5%', position:'relative', overflow:'hidden',
 }}><div style={{ maxWidth:'1200px', width:'100%', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:'60px', alignItems:'center' }}>
 {/* Text LEFT */}
 <div style={{ zIndex:2 }}><div style={{ fontSize:'0.75em', fontWeight:800, letterSpacing:'3px', textTransform:'uppercase', color:'#a48bff', marginBottom:'18px' }}> Agency Service</div><h2 style={{ fontSize:'clamp(2.6rem, 5vw, 4rem)', fontWeight:200, color:'#f0f4ff', lineHeight:1.05, letterSpacing:'-2px', marginBottom:'22px' }}>Custom AI Chatbots</h2><p style={{ fontSize:'clamp(1.05rem, 1.7vw, 1.35rem)', color:'#c0c8dc', lineHeight:1.55, fontWeight:300, maxWidth:'520px', marginBottom:'40px' }}>
 Trained on your data. Deployed to your website, WhatsApp, Slack, or Discord. Handles 70&percnt; of support tickets automatically. GPT-4, Claude, or Gemini-powered.
 </p><div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}><button onClick={()=>setModalOpen(true)} style={{ padding:'16px 40px', background:'#f0f4ff', color:'#050810', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Build Mine</button><button onClick={()=>navTo('services')} style={{ padding:'16px 40px', background:'transparent', color:'#f0f4ff', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'1px solid rgba(240,244,255,0.4)', cursor:'pointer', fontFamily:'inherit' }}>Learn More</button></div></div>
 {/* Chat mockup RIGHT */}
 <div style={{ position:'relative', zIndex:1 }}><div style={{
 background:'linear-gradient(135deg, #1a1230, #050810)',
 border:'1px solid rgba(124,58,237,0.3)',
 borderRadius:'20px',
 padding:'28px 24px',
 boxShadow:'0 40px 80px rgba(0,0,0,0.5), 0 0 60px rgba(124,58,237,0.18)',
 transform:'perspective(1200px) rotateY(-4deg) rotateX(2deg)',
 maxWidth:'420px',
 margin:'0 auto',
 }}><div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'18px', paddingBottom:'14px', borderBottom:'1px solid rgba(124,58,237,0.2)' }}><div style={{ width:'34px', height:'34px', borderRadius:'50%', background:'linear-gradient(135deg,#7c3aed,#ec4899)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem' }}></div><div style={{ fontSize:'.88rem', fontWeight:700, color:'#f0f4ff' }}>ClientBot · Online</div><span style={{ marginLeft:'auto', width:'8px', height:'8px', borderRadius:'50%', background:'#5bd4b8' }} /></div>
 {[
 { user:false, txt:'Hi! How can I help you with your order today?' },
 { user:true, txt:'When will my order ship?' },
 { user:false, txt:'Your order #4829 shipped this morning via FedEx. Tracking: 7847283. Estimated delivery Fri Aug 8.' },
 { user:true, txt:'Can you send the tracking to my email?' },
 { user:false, txt:' Done! Sent to sarah@example.com. Anything else?' },
 ].map((m,i)=>(
 <div key={i} style={{ display:'flex', justifyContent: m.user?'flex-end':'flex-start', marginBottom:'10px' }}><div style={{ maxWidth:'82%', padding:'10px 14px', borderRadius:'14px', fontSize:'.85rem', lineHeight:1.4, background: m.user?'rgba(124,58,237,0.25)':'rgba(240,244,255,0.06)', color:'#f0f4ff', border: `1px solid ${m.user?'rgba(124,58,237,0.3)':'rgba(240,244,255,0.08)'}` }}>{m.txt}</div></div>
 ))}
 <div style={{ marginTop:'14px', padding:'10px 12px', background:'rgba(91,212,184,0.08)', border:'1px solid rgba(91,212,184,0.2)', borderRadius:'10px', fontSize:'.76rem', color:'#c0c8dc' }}><strong style={{ color:'#5bd4b8' }}>70% resolved by AI</strong> · avg response 12 sec
 </div></div></div></div></section>

 {/* ═══ TESLA-STYLE SECTION — ALGORITHMIC TRADING BOTS ═══════════════════ */}
 <section style={{
 minHeight:'92vh',
 background:'radial-gradient(circle at 70% 40%, rgba(16,185,129,0.16), transparent 55%), radial-gradient(circle at 30% 60%, rgba(245,158,11,0.10), transparent 55%), #050810',
 display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 5%', position:'relative', overflow:'hidden',
 }}><div style={{ maxWidth:'1200px', width:'100%', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:'60px', alignItems:'center' }}>
 {/* Trading mockup LEFT */}
 <div style={{ position:'relative', zIndex:1, order:1 }}><div style={{
 background:'linear-gradient(135deg, #0f2318, #050810)',
 border:'1px solid rgba(16,185,129,0.3)',
 borderRadius:'20px',
 padding:'28px',
 boxShadow:'0 40px 80px rgba(0,0,0,0.5), 0 0 60px rgba(16,185,129,0.18)',
 transform:'perspective(1200px) rotateY(4deg) rotateX(2deg)',
 }}><div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}><div><div style={{ fontSize:'.7rem', color:'#10b981', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'6px' }}> Trading Bot · BTC/USD</div><div style={{ fontSize:'1.6rem', fontWeight:800, color:'#f0f4ff' }}>+$12,840 <span style={{ fontSize:'.85rem', color:'#5bd4b8', fontWeight:600 }}>30-day P&amp;L</span></div></div><span style={{ padding:'6px 10px', background:'rgba(16,185,129,0.15)', borderRadius:'6px', fontSize:'.7rem', color:'#5bd4b8', fontWeight:700, letterSpacing:'0.5px' }}>LIVE</span></div>
 {/* Fake trading chart */}
 <svg viewBox="0 0 400 120" style={{ width:'100%', height:'auto', marginBottom:'16px' }}><path d="M 0 90 L 40 75 L 80 82 L 120 60 L 160 68 L 200 45 L 240 55 L 280 35 L 320 42 L 360 22 L 400 30" stroke="#10b981" strokeWidth="2" fill="none" /><path d="M 0 90 L 40 75 L 80 82 L 120 60 L 160 68 L 200 45 L 240 55 L 280 35 L 320 42 L 360 22 L 400 30 L 400 120 L 0 120 Z" fill="url(#tradeGrad)" opacity="0.3" /><defs><linearGradient id="tradeGrad" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#10b981" stopOpacity="0.5" /><stop offset="1" stopColor="#10b981" stopOpacity="0" /></linearGradient></defs></svg>
 {[
 { lbl:'Trades Executed', val:'247' },
 { lbl:'Win Rate', val:'62.3%', green:true },
 { lbl:'Sharpe Ratio', val:'1.84', green:true },
 { lbl:'Uptime', val:'99.9%', green:true },
 ].map((s,i)=>(
 <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 12px', marginBottom:'6px', background:'rgba(16,185,129,0.05)', border:'1px solid rgba(16,185,129,0.1)', borderRadius:'8px' }}><span style={{ color:'#c0c8dc', fontSize:'.82rem' }}>{s.lbl}</span><span style={{ color: s.green?'#5bd4b8':'#f0f4ff', fontWeight:800, fontSize:'.82rem' }}>{s.val}</span></div>
 ))}
 </div></div>
 {/* Text RIGHT */}
 <div style={{ zIndex:2, order:2 }}><div style={{ fontSize:'0.75em', fontWeight:800, letterSpacing:'3px', textTransform:'uppercase', color:'#10b981', marginBottom:'18px' }}> Agency Service</div><h2 style={{ fontSize:'clamp(2.6rem, 5vw, 4rem)', fontWeight:200, color:'#f0f4ff', lineHeight:1.05, letterSpacing:'-2px', marginBottom:'22px' }}>Algorithmic Trading Bots</h2><p style={{ fontSize:'clamp(1.05rem, 1.7vw, 1.35rem)', color:'#c0c8dc', lineHeight:1.55, fontWeight:300, maxWidth:'520px', marginBottom:'40px' }}>
 Custom algo bots for crypto, forex, futures, and equities. Signal generation, backtesting, live execution on Binance, Kraken, Interactive Brokers, MT4/MT5. Risk management built-in.
 </p><div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}><button onClick={()=>setModalOpen(true)} style={{ padding:'16px 40px', background:'#f0f4ff', color:'#050810', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Get Quote</button><button onClick={()=>navTo('services')} style={{ padding:'16px 40px', background:'transparent', color:'#f0f4ff', fontWeight:600, fontSize:'.95rem', letterSpacing:'1px', textTransform:'uppercase', borderRadius:'6px', border:'1px solid rgba(240,244,255,0.4)', cursor:'pointer', fontFamily:'inherit' }}>Learn More</button></div></div></div></section>

 {/* ═══ APPLE VISION-PRO STYLE — THE TRANSFORMATION ═══════════════════════ */}
 <section ref={transformRef} style={{
 position:'relative',
 background:'radial-gradient(ellipse at top, #0a1428 0%, #000 60%)',
 padding:'140px 5% 160px',
 overflow:'hidden',
 borderTop:'1px solid rgba(79,142,247,0.15)',
 borderBottom:'1px solid rgba(79,142,247,0.15)',
 }}>
 {/* Ambient glow orbs — parallax feel */}
 <div style={{ position:'absolute', top:'10%', left:'15%', width:'500px', height:'500px', background:'radial-gradient(circle, rgba(79,142,247,0.18), transparent 70%)', filter:'blur(80px)', pointerEvents:'none' }} />
 <div style={{ position:'absolute', bottom:'10%', right:'10%', width:'600px', height:'600px', background:'radial-gradient(circle, rgba(236,72,153,0.12), transparent 70%)', filter:'blur(90px)', pointerEvents:'none' }} />
 <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'900px', height:'900px', background:'radial-gradient(circle, rgba(164,139,255,0.08), transparent 60%)', filter:'blur(100px)', pointerEvents:'none' }} />

 <div style={{ maxWidth:'1400px', margin:'0 auto', position:'relative', zIndex:2 }}>

 {/* Apple-style headline */}
 <div className="anim anim-up" style={{ textAlign:'center', marginBottom:'100px' }}>
 <div style={{ fontSize:'0.72em', letterSpacing:'4px', textTransform:'uppercase', color:'#4f8ef7', fontWeight:800, marginBottom:'24px' }}>The Transformation</div>
 <h2 style={{
 fontSize:'clamp(2.6rem, 6.5vw, 6rem)',
 fontWeight:100,
 color:'#f0f4ff',
 lineHeight:1.02,
 letterSpacing:'-3px',
 margin:'0 auto 28px',
 maxWidth:'1100px'
 }}>
 See what AI automation<br/>
 <span className="mkt-gradient-text">actually fixes.</span>
 </h2>
 <p style={{ fontSize:'clamp(1.1rem, 1.6vw, 1.4rem)', color:'#8a94b0', maxWidth:'720px', margin:'0 auto', fontWeight:300, lineHeight:1.55 }}>
 The average business wastes 500+ hours per month on work AI can do in seconds. Here's the shift, side by side.
 </p>
 </div>

 {/* Before / After split-panel */}
 <div className="mkt-transform-split" style={{
 display:'grid',
 gridTemplateColumns:'1fr auto 1fr',
 gap:'40px',
 alignItems:'stretch',
 marginBottom:'140px'
 }}>

 {/* BEFORE panel */}
 <div className="anim anim-left" style={{
 background:'linear-gradient(180deg, rgba(220,38,38,0.10), rgba(220,38,38,0.02))',
 border:'1px solid rgba(239,68,68,0.28)',
 borderRadius:'24px',
 padding:'52px 40px',
 position:'relative',
 overflow:'hidden',
 backdropFilter:'blur(20px)',
 }}>
 <div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:'linear-gradient(90deg, transparent, #ef4444, transparent)' }} />
 <div style={{ fontSize:'0.68em', letterSpacing:'3px', textTransform:'uppercase', color:'#ef4444', fontWeight:800, marginBottom:'14px' }}>Before Gantima</div>
 <h3 style={{ fontSize:'2.2rem', fontWeight:200, color:'#f0f4ff', margin:'0 0 36px', letterSpacing:'-1.5px' }}>Manual chaos</h3>
 {[
 { label:'Hours wasted / month', val:'520+' },
 { label:'Invoices processed / day', val:'40' },
 { label:'Error rate', val:'8–12%' },
 { label:'Ticket response time', val:'6 hrs' },
 { label:'Team burnout risk', val:'High' },
 ].map((r,i)=>(
 <div key={i} style={{
 display:'flex', justifyContent:'space-between', alignItems:'center',
 padding:'20px 0', borderBottom: i===4 ? 'none' : '1px solid rgba(239,68,68,0.15)'
 }}>
 <span style={{ color:'#c0c8dc', fontSize:'.95rem' }}>{r.label}</span>
 <span style={{ color:'#ef4444', fontWeight:800, fontSize:'1.4rem', letterSpacing:'-0.5px' }}>{r.val}</span>
 </div>
 ))}
 </div>

 {/* Center arrow — pulsing glow */}
 <div className="anim anim-scale" style={{
 display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'18px',
 alignSelf:'center', minWidth:'100px'
 }}>
 <div className="mkt-transform-arrow" style={{
 width:'72px', height:'72px', borderRadius:'50%',
 background:'linear-gradient(135deg, #4f8ef7, #a48bff, #ec4899)',
 display:'flex', alignItems:'center', justifyContent:'center',
 }}>
 <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
 <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
 </svg>
 </div>
 <div style={{ fontSize:'.7rem', letterSpacing:'2.5px', textTransform:'uppercase', color:'#a48bff', fontWeight:800, textAlign:'center', lineHeight:1.4 }}>Gantima<br/>AI</div>
 </div>

 {/* AFTER panel */}
 <div className="anim anim-right" style={{
 background:'linear-gradient(180deg, rgba(16,185,129,0.10), rgba(16,185,129,0.02))',
 border:'1px solid rgba(16,185,129,0.28)',
 borderRadius:'24px',
 padding:'52px 40px',
 position:'relative',
 overflow:'hidden',
 backdropFilter:'blur(20px)',
 }}>
 <div style={{ position:'absolute', top:0, left:0, right:0, height:'3px', background:'linear-gradient(90deg, transparent, #10b981, transparent)' }} />
 <div style={{ fontSize:'0.68em', letterSpacing:'3px', textTransform:'uppercase', color:'#10b981', fontWeight:800, marginBottom:'14px' }}>After Gantima</div>
 <h3 style={{ fontSize:'2.2rem', fontWeight:200, color:'#f0f4ff', margin:'0 0 36px', letterSpacing:'-1.5px' }}>Automated harmony</h3>
 {[
 { label:'Hours wasted / month', val:'0' },
 { label:'Invoices processed / day', val:'∞ auto' },
 { label:'Error rate', val:'<0.5%' },
 { label:'Ticket response time', val:'2 min' },
 { label:'Team burnout risk', val:'None' },
 ].map((r,i)=>(
 <div key={i} style={{
 display:'flex', justifyContent:'space-between', alignItems:'center',
 padding:'20px 0', borderBottom: i===4 ? 'none' : '1px solid rgba(16,185,129,0.15)'
 }}>
 <span style={{ color:'#c0c8dc', fontSize:'.95rem' }}>{r.label}</span>
 <span style={{ color:'#10b981', fontWeight:800, fontSize:'1.4rem', letterSpacing:'-0.5px' }}>{r.val}</span>
 </div>
 ))}
 </div>
 </div>

 {/* Workflow reveal — a real automation, step by step */}
 <div style={{ marginBottom:'120px' }}>
 <div className="anim anim-up" style={{ textAlign:'center', marginBottom:'70px' }}>
 <div style={{ fontSize:'0.68em', letterSpacing:'3px', textTransform:'uppercase', color:'#a48bff', fontWeight:800, marginBottom:'18px' }}>A Real Workflow</div>
 <h3 style={{ fontSize:'clamp(1.9rem, 3.5vw, 3rem)', fontWeight:200, color:'#f0f4ff', letterSpacing:'-1.8px', margin:'0 0 16px' }}>
 Invoice → paid. In <span className="mkt-gradient-text">eight seconds.</span>
 </h3>
 <p style={{ fontSize:'clamp(1rem, 1.4vw, 1.15rem)', color:'#8a94b0', maxWidth:'600px', margin:'0 auto', fontWeight:300 }}>
 What used to take a person 12 minutes now finishes before you refresh your inbox.
 </p>
 </div>

 <div style={{
 display:'grid',
 gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',
 gap:'24px'
 }}>
 {[
 { time:'0s', title:'Email arrives', desc:'Invoice from vendor lands in a monitored inbox', color:'#4f8ef7' },
 { time:'2s', title:'AI extracts data', desc:'Vendor name, amount, PO number, line items parsed', color:'#7c3aed' },
 { time:'4s', title:'Matched + validated', desc:'Cross-checked against open PO in the ERP', color:'#a48bff' },
 { time:'8s', title:'Posted + paid', desc:'Approved, posted to accounting, payment scheduled', color:'#10b981' },
 ].map((step,i)=>(
 <div key={i} className="anim anim-up mkt-flow-step" style={{
 background:'rgba(15,23,42,0.7)',
 border:`1px solid ${step.color}40`,
 borderRadius:'18px',
 padding:'36px 28px 32px',
 position:'relative',
 backdropFilter:'blur(12px)',
 }}>
 <div style={{
 position:'absolute', top:'-14px', left:'26px',
 padding:'5px 14px', background:step.color, color:'#fff',
 fontSize:'.7rem', fontWeight:800, letterSpacing:'1.5px', borderRadius:'8px',
 boxShadow:`0 8px 24px ${step.color}60`,
 }}>{step.time}</div>
 <div style={{
 width:'48px', height:'48px', borderRadius:'12px',
 background:`linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
 border:`1px solid ${step.color}60`,
 display:'flex', alignItems:'center', justifyContent:'center',
 marginBottom:'22px', fontSize:'1.5rem', fontWeight:800, color:step.color
 }}>{i+1}</div>
 <h4 style={{ fontSize:'1.15rem', fontWeight:700, color:'#f0f4ff', margin:'0 0 10px', letterSpacing:'-0.3px' }}>{step.title}</h4>
 <p style={{ fontSize:'.92rem', color:'#8a94b0', lineHeight:1.55, margin:0, fontWeight:300 }}>{step.desc}</p>
 </div>
 ))}
 </div>
 </div>

 {/* Big transformation metrics — Vision Pro-style */}
 <div className="anim anim-scale" style={{
 background:'linear-gradient(135deg, rgba(79,142,247,0.10), rgba(124,58,237,0.08), rgba(236,72,153,0.06))',
 border:'1px solid rgba(79,142,247,0.25)',
 borderRadius:'32px',
 padding:'72px 48px',
 backdropFilter:'blur(20px)',
 position:'relative',
 overflow:'hidden',
 }}>
 <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg, transparent, #4f8ef7, #a48bff, #ec4899, transparent)' }} />
 <div style={{ textAlign:'center', marginBottom:'60px' }}>
 <div style={{ fontSize:'0.68em', letterSpacing:'3px', textTransform:'uppercase', color:'#ec4899', fontWeight:800, marginBottom:'16px' }}>The Numbers</div>
 <h3 style={{ fontSize:'clamp(1.8rem, 3vw, 2.6rem)', fontWeight:200, color:'#f0f4ff', letterSpacing:'-1.5px', margin:0 }}>
 What our clients get, on average.
 </h3>
 </div>
 <div style={{
 display:'grid',
 gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',
 gap:'40px'
 }}>
 {[
 { num:t1, suffix:'+', label:'Hours saved per month' },
 { num:t2, suffix:'×', label:'Faster than manual work' },
 { num:t3, suffix:'%', label:'Fewer human errors' },
 { num:t4, suffix:'', label:'Days from kickoff to live', prefix:'' },
 ].map((s,i)=>(
 <div key={i} style={{ textAlign:'center' }}>
 <div className="mkt-gradient-text" style={{
 fontSize:'clamp(3.2rem, 6.5vw, 5.5rem)',
 fontWeight:100,
 lineHeight:1,
 letterSpacing:'-4px',
 marginBottom:'14px'
 }}>
 {s.prefix||''}{s.num}{s.suffix}
 </div>
 <div style={{ fontSize:'.82rem', color:'#8a94b0', letterSpacing:'1.5px', textTransform:'uppercase', fontWeight:700 }}>
 {s.label}
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 </section>

 {/* ── Stats Bar ─────────────────────────────── */}
 <section className="mkt-stats-bar" ref={statsRef}><div className="mkt-container"><div className="mkt-stats-grid"><div className="mkt-stats-item"><span className="mkt-stats-num">{c1}+</span><span className="mkt-stats-label">Automations Deployed</span></div><div className="mkt-stats-divider" /><div className="mkt-stats-item"><span className="mkt-stats-num">{c2}+</span><span className="mkt-stats-label">Happy Clients</span></div><div className="mkt-stats-divider" /><div className="mkt-stats-item"><span className="mkt-stats-num">${c3}M+</span><span className="mkt-stats-label">Client Value Generated</span></div><div className="mkt-stats-divider" /><div className="mkt-stats-item"><span className="mkt-stats-num">{c4}%</span><span className="mkt-stats-label">Client Retention Rate</span></div></div></div></section>

 {/* ── Industries Showcase ──────────────────── */}
 <section style={{ background: '#020817', padding: '60px 5% 0', textAlign: 'center' }}><div className="mkt-container">
 {/* Trigger button */}
 <div
 onClick={() => setIndustriesOpen(o => !o)}
 style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}
 >
 {/* Icon circle */}
 <div style={{
 width: '72px', height: '72px', borderRadius: '50%',
 background: 'linear-gradient(135deg,#4f8ef7,#7c3aed)',
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: '1.8rem',
 boxShadow: industriesOpen ? '0 0 0 8px rgba(79,142,247,0.15), 0 0 32px rgba(124,58,237,0.4)' : '0 0 0 0px rgba(79,142,247,0)',
 transition: 'box-shadow .3s, transform .3s',
 transform: industriesOpen ? 'scale(1.08)' : 'scale(1)',
 }}></div><div style={{ fontSize: '.8rem', fontWeight: 700, color: '#4f8ef7', textTransform: 'uppercase', letterSpacing: '.12em' }}>
 Industries We Serve
 </div><div style={{
 fontSize: '1.2rem', color: '#64748b',
 transform: industriesOpen ? 'rotate(180deg)' : 'rotate(0)',
 transition: 'transform .3s',
 lineHeight: 1,
 }}>▾</div></div>

 {/* Expandable grid */}
 <div style={{
 overflow: 'hidden',
 maxHeight: industriesOpen ? '2000px' : '0',
 transition: 'max-height .5s cubic-bezier(0.4,0,0.2,1)',
 }}><div style={{ paddingTop: '40px', paddingBottom: '60px' }}><p style={{ color: '#64748b', fontSize: '.95rem', marginBottom: '32px', maxWidth: '560px', margin: '0 auto 32px' }}>
 We build AI automation and custom software for every major industry. Click any card to see what we can do for your sector.
 </p><div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
 gap: '14px', maxWidth: '1100px', margin: '0 auto',
 }}>
 {Object.entries(industryData).map(([name, data]) => (
 <div
 key={name}
 onClick={e => { e.stopPropagation(); setActiveIndustry(activeIndustry === name ? null : name); }}
 style={{
 padding: '20px 16px',
 background: activeIndustry === name ? `rgba(79,142,247,0.1)` : 'rgba(255,255,255,0.03)',
 border: `1px solid ${activeIndustry === name ? data.color : 'rgba(255,255,255,0.07)'}`,
 borderRadius: '16px', cursor: 'pointer', textAlign: 'left',
 transition: 'all .2s',
 }}
 onMouseEnter={e => { if (activeIndustry !== name) e.currentTarget.style.borderColor = data.color; }}
 onMouseLeave={e => { if (activeIndustry !== name) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
 ><div style={{ fontSize: '1.6rem', marginBottom: '10px' }}>{data.icon}</div><div style={{ fontSize: '.85rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '4px', lineHeight: 1.3 }}>{name}</div><div style={{ fontSize: '.72rem', color: data.color, fontWeight: 600 }}>
 {data.products.length} solutions →
 </div></div>
 ))}
 </div>

 {/* Detail panel */}
 {activeIndustry && (
 <div style={{
 marginTop: '28px', maxWidth: '900px', margin: '28px auto 0',
 background: 'rgba(79,142,247,0.05)',
 border: `1px solid ${industryData[activeIndustry].color}40`,
 borderRadius: '20px', padding: '32px',
 textAlign: 'left', animation: 'fadeSlideUp .3s ease',
 }}><div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' }}><div style={{
 width: '52px', height: '52px', borderRadius: '14px',
 background: `${industryData[activeIndustry].color}20`,
 border: `1px solid ${industryData[activeIndustry].color}40`,
 display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0,
 }}>{industryData[activeIndustry].icon}</div><div><div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#f1f5f9' }}>{activeIndustry}</div><div style={{ fontSize: '.78rem', color: industryData[activeIndustry].color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>AI Automation Solutions</div></div></div><p style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '.9rem', marginBottom: '20px' }}>
 {industryData[activeIndustry].desc}
 </p><div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
 {industryData[activeIndustry].products.map((p, i) => {
   const isViral = i === 0 && p.startsWith('ViralVolt');
   const isInvestiy = i === 0 && p.startsWith('Investiy');
   const isFeatured = isViral || isInvestiy;
   const featColor = isInvestiy ? '#a3e635' : '#a78bfa';
   const featBg    = isInvestiy ? 'rgba(163,230,53,0.12)' : 'rgba(124,58,237,0.12)';
   const featBorder = isInvestiy ? 'rgba(163,230,53,0.30)' : 'rgba(124,58,237,0.30)';
   return (
 <span key={i} style={{
 fontSize: '.78rem', fontWeight: 600,
 color: isFeatured ? featColor : '#94a3b8',
 background: isFeatured ? featBg : 'rgba(255,255,255,0.05)',
 border: `1px solid ${isFeatured ? featBorder : 'rgba(255,255,255,0.08)'}`,
 borderRadius: '999px', padding: '5px 14px',
 }}>
 {p}
 </span>
   );
 })}
 </div><button
 className="mkt-btn mkt-btn-primary mkt-btn-glow"
 onClick={() => setModalOpen(true)}
 style={{ background: `linear-gradient(135deg,${industryData[activeIndustry].color},#7c3aed)` }}
 >
 Get a Free {activeIndustry} Automation Audit →
 </button></div>
 )}
 </div></div></div></section>

 {/* ── Marquee ────────────────────────────────── */}
 <div className="mkt-marquee-wrapper"><div className="mkt-marquee-label">POWERED BY</div><div className="mkt-marquee"><div className="mkt-marquee-track">
 {[...techStack, ...techStack].map((t, i) => (
 <div key={i} className="mkt-marquee-item"><span className="mkt-marquee-icon">{t.icon}</span><span className="mkt-marquee-name">{t.name}</span></div>
 ))}
 </div></div></div>

 {/* ── Sample Workflow ─────────────────────────── */}
 <section className="mkt-section" id="workflow"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Sample Workflow</div><h2>Invoice Automation in Action</h2><p>See how a real automation workflow processes invoices end-to-end with zero manual work.</p></div><div className="mkt-workflow-row">
 {workflowSteps.map((s, i) => (
 <React.Fragment key={i}><div className="mkt-workflow-card anim anim-up"><span style={{ fontSize: '2rem' }}>{s.icon}</span><h4>{s.title}</h4><p>{s.desc}</p></div>
 {i < workflowSteps.length - 1 && <div className="mkt-workflow-arrow">→</div>}
 </React.Fragment>
 ))}
 </div><div className="mkt-workflow-result"> Result: Saves 40 hours per month | Zero errors | 100% hands-off</div></div></section>

 {/* ── Services ───────────────────────────────── */}
 <section className="mkt-section mkt-services" id="services"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">What We Build</div><h2>AI Automation Services</h2><p>Six focused service lines. One goal: make your business run itself.</p></div><div className="mkt-services-grid">
 {services.map((s, i) => (
 <div className="mkt-service-card anim anim-up" key={i} style={{ '--accent': s.color, ...(s.badge ? { border: '1px solid rgba(236,72,153,0.25)' } : {}) }}><div className="mkt-service-accent-bar" /><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}><div className="mkt-service-icon-wrap" style={{ margin: 0 }}><span className="mkt-service-icon">{s.icon}</span></div>
 {s.badge && (
 <span style={{ fontSize: '.68rem', fontWeight: 700, color: '#a78bfa', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '999px', padding: '3px 9px', whiteSpace: 'nowrap' }}>
 {s.badge}
 </span>
 )}
 </div><h3>{s.title}</h3><p>{s.desc}</p><div className="mkt-service-tags">{s.tags.map(t =><span key={t} className="mkt-tag">{t}</span>)}</div>
 {s.badge
 ? <a className="mkt-service-link" href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" style={{ color: '#ec4899' }}>Explore ViralVolt →</a>
 : <button className="mkt-service-link" onClick={() => setModalOpen(true)}>Get started →</button>
 }
 </div>
 ))}
 </div></div></section>

 {/* ── Why Choose Us ──────────────────────────── */}
 <section className="mkt-section mkt-why" id="why"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Why Gantima</div><h2>Built Different. By Design.</h2><p>We're not a general-purpose dev shop. We're specialists who eat, sleep, and breathe AI automation.</p></div><div className="mkt-why-grid">
 {whyUs.map((w, i) => (
 <div className="mkt-why-card anim anim-up" key={i}><div className="mkt-why-icon">{w.icon}</div><div className="mkt-why-content"><h3>{w.title}</h3><p>{w.desc}</p><div className="mkt-why-vs"><span className="mkt-why-vs-badge">vs.</span><span className="mkt-why-vs-text">{w.vs}</span></div></div></div>
 ))}
 </div></div></section>

 {/* ── How It Works ───────────────────────────── */}
 <section className="mkt-section mkt-how" id="how"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">The Process</div><h2>From Audit to Automation in 2 Weeks</h2><p>A proven 4-step process that delivers results — fast, without disruption.</p></div><div className="mkt-steps-grid">
 {steps.map((s, i) => (
 <div className="mkt-step-card anim anim-up" key={i}><div className="mkt-step-num-wrap"><span className="mkt-step-num">{s.n}</span><span className="mkt-step-icon">{s.icon}</span></div><h3>{s.title}</h3><p>{s.desc}</p></div>
 ))}
 </div><div className="mkt-how-cta"><button className="mkt-btn mkt-btn-primary mkt-btn-lg mkt-btn-glow" onClick={() => setModalOpen(true)}>Start With a Free Audit</button></div></div></section>

 {/* ── Live Client Deployments ─────────────────── */}
 <section className="mkt-section mkt-examples" id="examples"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Real Results</div><h2>Live Client Deployments</h2><p>Real workflows built and running for clients — not demos, not templates.</p></div><div className="mkt-examples-grid">
 {examples.map((ex, i) => (
 <div className="mkt-example-card anim anim-up" key={i}><div className="mkt-example-top"><span className="mkt-example-icon">{ex.icon}</span><span className="mkt-example-save">{ex.save}</span></div><h3>{ex.label}</h3><p>{ex.desc}</p><div className="mkt-example-metric"><span className="mkt-example-metric-dot">●</span>{ex.metric}</div></div>
 ))}
 </div></div></section>

 {/* ── Team ───────────────────────────────────── */}
 <section className="mkt-section mkt-team" id="team"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Our Team</div><h2>Meet The Experts Behind Your Automation</h2><p>AI engineers, architects, and automation specialists dedicated to your success.</p></div><div className="mkt-team-grid">
 {team.map((t, i) => (
 <div className="mkt-team-card anim anim-up" key={i}><div style={{
 width:'88px',
 height:'88px',
 borderRadius:'50%',
 background: t.gradient,
 display:'flex',
 alignItems:'center',
 justifyContent:'center',
 fontSize:'1.55rem',
 fontWeight:900,
 color:'#fff',
 letterSpacing:'1px',
 margin:'0 auto 16px',
 boxShadow:`0 12px 32px ${t.glow}, inset 0 -6px 20px rgba(0,0,0,0.15), inset 0 4px 16px rgba(255,255,255,0.15)`,
 border:'1px solid rgba(255,255,255,0.08)',
 position:'relative',
 overflow:'hidden',
 }}>
 {/* Subtle inner glow overlay */}
 <div style={{ position:'absolute', top:0, left:0, right:0, height:'50%', background:'linear-gradient(to bottom, rgba(255,255,255,0.15), transparent)', pointerEvents:'none' }} /><span style={{ position:'relative', zIndex:1, textShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>{t.initials}</span></div><div className="mkt-team-name">{t.name}</div><div className="mkt-team-role">{t.role}</div><p style={{ fontSize: '.78rem', color: '#94a3b8', margin: 0 }}>{t.desc}</p></div>
 ))}
 </div></div></section>

 {/* ── Live Monitoring Dashboard ──────────────── */}
 <section className="mkt-section mkt-monitoring" id="monitoring"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Live Monitoring</div><h2>Real-Time Automation Dashboard</h2><p>Track your automations 24/7 with ARIA, our AI monitoring system.</p></div><div className="mkt-monitor-grid">
 {monitors.map((m, i) => (
 <div className="mkt-monitor-card anim anim-up" key={i}><div className="mkt-monitor-header"><span className="mkt-monitor-title">{m.title}</span><span className="mkt-monitor-status"><span className="mkt-live-dot" /> LIVE</span></div>
 {m.stats.map((s, j) => (
 <div className="mkt-monitor-stat" key={j}><span className="mkt-monitor-label">{s.label}</span><span className="mkt-monitor-value" style={{ color: s.green ? 'var(--mkt-green)' : s.amber ? '#f59e0b' : undefined }}>{s.value}</span></div>
 ))}
 </div>
 ))}
 </div></div></section>

 {/* ── Pricing ────────────────────────────────── */}
 <section className="mkt-section mkt-pricing" id="pricing"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Transparent Pricing</div><h2>Choose Your Automation Plan</h2><p>Predictable monthly retainers. No hidden fees, no lock-in after month 3.</p></div><div className="mkt-pricing-grid">
 {pricing.map((plan, i) => (
 <div className={`mkt-pricing-card anim anim-up${plan.badge ? ' mkt-pricing-featured' : ''}`} key={i}>
 {plan.badge && <div className="mkt-pricing-badge">{plan.badge}</div>}
 <h3 className="mkt-pricing-name">{plan.name}</h3><div className="mkt-pricing-price"><span className="mkt-price-amount" style={{ color: plan.color }}>{plan.price}</span><span className="mkt-price-sub">{plan.sub}</span></div><ul className="mkt-pricing-features">{plan.features.map(f =><li key={f}><span className="mkt-check"></span> {f}</li>)}</ul><button className={`mkt-btn mkt-btn-lg ${plan.outline ? 'mkt-btn-outline' : 'mkt-btn-primary'}`} onClick={() => setModalOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>{plan.cta}</button></div>
 ))}
 </div><p className="mkt-pricing-note">All plans start with a free automation audit. Cancel anytime after month 3.</p></div></section>

 {/* ── Industries ─────────────────────────────── */}
 <section className="mkt-section mkt-industries" id="industries"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Who We Serve</div><h2>Industries We Automate</h2></div><div className="mkt-industries-grid">
 {industries.map(ind =><div className="mkt-industry-chip" key={ind}>{ind}</div>)}
 </div></div></section>

 {/* ── Testimonials ───────────────────────────── */}
 <section className="mkt-section mkt-testimonials" id="testimonials"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">Client Results</div><h2>Real ROI. Real Clients.</h2><p>Don't take our word for it — here's what happened when real businesses automated with Gantima.</p></div><div className="mkt-testimonials-grid">
 {testimonials.map((t, i) => (
 <div className="mkt-testimonial-card anim anim-up" key={i}><div className="mkt-testimonial-metric-badge"><span className="mkt-tm-num">{t.metric}</span><span className="mkt-tm-label">{t.metricLabel}</span></div><div className="mkt-testimonial-stars"></div><p className="mkt-testimonial-quote">"{t.quote}"</p><div className="mkt-testimonial-author"><div className="mkt-testimonial-avatar">{t.name.split(' ')[0][0]}{t.name.split(' ')[1]?.[0]}</div><div><div className="mkt-testimonial-name">{t.name}</div><div className="mkt-testimonial-role">{t.role}</div></div></div></div>
 ))}
 </div></div></section>

 {/* ── FAQ ────────────────────────────────────── */}
 <section className="mkt-section mkt-faq" id="faq"><div className="mkt-container"><div className="mkt-section-header anim anim-up"><div className="mkt-pill">FAQ</div><h2>Questions We Get Asked a Lot</h2><p>Everything you need to know before booking your free consultation.</p></div><div className="mkt-faq-list">
 {faqs.map((f, i) => (
 <div className={`mkt-faq-item${faqOpen === i ? ' mkt-faq-open' : ''}`} key={i}><button className="mkt-faq-q" onClick={() => setFaqOpen(faqOpen === i ? null : i)}><span>{f.q}</span><span className="mkt-faq-icon">{faqOpen === i ? '−' : '+'}</span></button>
 {faqOpen === i && <div className="mkt-faq-a"><p>{f.a}</p></div>}
 </div>
 ))}
 </div></div></section>

 {/* ── CTA Banner ─────────────────────────────── */}
 <section className="mkt-cta-banner"><div className="mkt-cta-orb mkt-cta-orb-1" /><div className="mkt-cta-orb mkt-cta-orb-2" /><div className="mkt-container mkt-cta-inner"><div className="mkt-cta-badge"> Limited spots available each month</div><h2>Ready to Stop Doing Work Machines Can Do?</h2><p>Join 150+ companies running smarter with Gantima AI automation. Start with a free consultation — no commitment required.</p><div className="mkt-cta-actions"><button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" onClick={() => setModalOpen(true)}> Book Your Free Consultation</button><span className="mkt-cta-note">No credit card · No commitment · 30-minute call</span></div></div></section>

 {/* ── Footer ─────────────────────────────────── */}
 <footer className="mkt-footer"><div className="mkt-container"><div className="mkt-footer-grid"><div className="mkt-footer-brand"><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'14px', textDecoration:'none' }}><img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'52px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 14px rgba(79,142,247,0.5))', flexShrink:0 }} /></a><p>AI automation agency helping businesses eliminate manual work and scale intelligently.</p><a href="mailto:support@gantima.com" className="mkt-footer-email">support@gantima.com</a></div><div className="mkt-footer-col"><h4>Services</h4><ul><li><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>Business Automation</a></li><li><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>AI Chatbots</a></li><li><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>SaaS Products</a></li><li><a href="#services" onClick={e => { e.preventDefault(); navTo('services'); }}>AI Consulting</a></li></ul></div><div className="mkt-footer-col"><h4>Company</h4><ul><li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li><li><a href="#how" onClick={e => { e.preventDefault(); navTo('how'); }}>How It Works</a></li><li><a href="#faq" onClick={e => { e.preventDefault(); navTo('faq'); }}>FAQ</a></li><li><a href="#pricing" onClick={e => { e.preventDefault(); navTo('pricing'); }}>Pricing</a></li></ul></div><div className="mkt-footer-col"><h4>Contact</h4><ul><li><a href="mailto:support@gantima.com">support@gantima.com</a></li></ul></div></div><div className="mkt-footer-bottom"><span>© 2026 Gantima Software. All rights reserved.</span><span><a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b' }}>Privacy Policy</a>
 {' · '}
 <a href="/terms" onClick={e => { e.preventDefault(); navigate('/terms'); }} style={{ color: '#64748b' }}>Terms of Service</a></span></div></div></footer>

 {/* ═══ TESLA-STYLE STICKY BOTTOM CTA (appears on scroll) ═══════════════ */}
 <div style={{
 position:'fixed',
 bottom:0,
 left:0,
 right:0,
 background:'rgba(5,8,16,0.85)',
 backdropFilter:'blur(20px) saturate(1.4)',
 borderTop:'1px solid rgba(79,142,247,0.15)',
 padding:'14px 5%',
 zIndex:100,
 display:'flex',
 alignItems:'center',
 justifyContent:'space-between',
 gap:'20px',
 flexWrap:'wrap',
 }}><div style={{ display:'flex', alignItems:'center', gap:'12px' }}><img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'28px', width:'auto', filter:'drop-shadow(0 0 8px rgba(79,142,247,0.4))' }} /><div style={{ display:'flex', flexDirection:'column' }}><span style={{ fontSize:'.85rem', fontWeight:800, color:'#f0f4ff', letterSpacing:'0.5px' }}>Gantima Software</span><span style={{ fontSize:'.68rem', color:'#7a85a0', fontWeight:500 }}>Ready to automate?</span></div></div><div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}><a href="https://viralvolt.ai" target="_blank" rel="noopener noreferrer" style={{
 padding:'10px 22px',
 background:'transparent',
 color:'#f0f4ff',
 fontWeight:600,
 fontSize:'.82rem',
 letterSpacing:'0.8px',
 textTransform:'uppercase',
 borderRadius:'6px',
 textDecoration:'none',
 border:'1px solid rgba(240,244,255,0.3)',
 }}>Try Viral Volt</a><button onClick={() => setModalOpen(true)} style={{
 padding:'10px 22px',
 background:'#f0f4ff',
 color:'#050810',
 fontWeight:700,
 fontSize:'.82rem',
 letterSpacing:'0.8px',
 textTransform:'uppercase',
 borderRadius:'6px',
 border:'none',
 cursor:'pointer',
 fontFamily:'inherit',
 }}>Book Free Audit</button></div></div>

 {/* ── Booking Modal ───────────────────────────── */}
 {modalOpen && (
 <div className="mkt-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}><div className="mkt-modal"><div className="mkt-modal-header"><div><h3>Book Your Free Consultation</h3><p>Tell us about your business and we'll identify your top automation opportunities.</p></div><button className="mkt-modal-close" onClick={() => setModalOpen(false)}></button></div>
 {formStatus === 'success' ? (
 <div className="mkt-form-success"><div className="mkt-success-icon"></div><h4>Consultation Requested!</h4><p>We'll be in touch within 24 hours to schedule your free consultation.</p></div>
 ) : (
 <form className="mkt-modal-form" onSubmit={handleSubmit}><div className="mkt-form-row"><div className="mkt-form-group"><label>Full Name *</label><input name="name" value={formData.name} onChange={handleInput} placeholder="Jane Smith" required /></div><div className="mkt-form-group"><label>Company *</label><input name="company" value={formData.company} onChange={handleInput} placeholder="Acme Corp" required /></div></div><div className="mkt-form-row"><div className="mkt-form-group"><label>Work Email *</label><input type="email" name="email" value={formData.email} onChange={handleInput} placeholder="jane@acme.com" required /></div><div className="mkt-form-group"><label>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleInput} placeholder="+1 (555) 000-0000" /></div></div><div className="mkt-form-group"><label>Automation Interests</label><div className="mkt-topic-chips">{topics.map(t => (<button key={t} type="button" className={`mkt-topic-chip${selectedTopics.includes(t) ? ' active' : ''}`} onClick={() => toggleTopic(t)}>{t}</button>))}</div><input type="text" name="customTopic" placeholder="Don't see yours? Type your automation here..." style={{ marginTop: '12px', width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(79,142,247,0.2)', borderRadius: '10px', color: '#e2e8f0', fontSize: '.875rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} /></div><div className="mkt-form-group"><label>Describe Your Biggest Manual Bottleneck</label><textarea name="description" value={formData.description} onChange={handleInput} rows={3} placeholder="e.g. We manually process 200 invoices per week across 3 systems..." /></div><div className="mkt-form-row"><div className="mkt-form-group"><label>Team Size</label><select name="teamSize" value={formData.teamSize} onChange={handleInput}><option value="">Select...</option><option>1–10</option><option>11–50</option><option>51–200</option><option>200+</option></select></div><div className="mkt-form-group"><label>Monthly Budget</label><select name="budget" value={formData.budget} onChange={handleInput}><option value="">Select...</option><option>Under $2,000</option><option>$2,000–$5,000</option><option>$5,000–$15,000</option><option>$15,000+</option></select></div></div>
 {formStatus === 'error' && <p className="mkt-form-error">Something went wrong. Please email us at <a href="mailto:support@gantima.com">support@gantima.com</a></p>}
 <button type="submit" className="mkt-btn mkt-btn-primary mkt-btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={formStatus === 'sending'}>{formStatus === 'sending' ? 'Sending...' : ' Request My Free Audit'}</button></form>
 )}
 </div></div>
 )}
 </div>
 );
}
